import { normalizeIntroPhone } from '../sdk/intro-phone';
import { classifyComposerSubmit, EMAIL_RE, type IdentityAskState } from '../sdk/identity-ask';
import type { EmailCheck } from '../sdk/email-check';
import { useEmailRefused } from './use-email-refused';
import { IdentityComposerHint, useIdentityDraft } from './IdentityComposer';
/**
 * DemoView - Product demo with fake browser frame + collapsible chat sidebar
 *
 * Layout (sidebar open):
 *  ┌──────────────────────────────────────┬──────────────────────┐
 *  │  ● ● ●   product.example.com    ⟳  │  Floe  ● Speaking  ─ │
 *  │  ┌──────────────────────────────┐   ├──────────────────────┤
 *  │  │                              │   │  Chat messages       │
 *  │  │     Video (screencast)       │   │  ...                 │
 *  │  │                              │   ├──────────────────────┤
 *  │  └──────────────────────────────┘   │  🎤 input   [End]   │
 *  └──────────────────────────────────────┴──────────────────────┘
 *
 * Layout (sidebar collapsed): video fills screen, floating expand button
 */

import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  sidebarContainerLayout,
  shouldAutoOpenPostDemoSidebar,
  headerButtonClosesDemo,
  shouldShowDockedPill,
  planDemoIntro,
  captureIsDeferred,
  END_CONFIRM_MODAL_Z,
  SIDEBAR_WIDTH,
} from './demoLayout';
import type { DemoIntroPrefill, DemoCapturePolicy } from './demoLayout';
import { lockBodyScroll } from '../sdk/scroll-lock';
import { HOST_DEMO_CTA_EVENT } from '../sdk/host-cta';
import { HOST_EXIT_INTENT_EVENT } from '../sdk/exit-intent';
import { deriveAccentVars } from '../sdk/branding';
import { getNamedString, getString } from '../sdk/ui-strings';
import { resolveCalendarEmbed } from '../sdk/calendar-embed';
import {
  openResolvedBookingWindow,
  resolveBookingIntentDestination,
  type BookingIntentResolver,
} from '../sdk/booking-attribution';
import { bookingUxHintFromMessage, type BookingUxHint } from '../sdk/booking-ux-hint';
import { AvatarPiP } from './AvatarPiP';
import { LanguageSelector } from './LanguageSelector';
import {
  resolveUntaggedOperatorCopy,
  type LanguageOption,
  type PendingLanguageSwitch,
} from '../sdk/language-preference';
import type { SupportedLanguage } from '../sdk/ui-strings';

marked.setOptions({ breaks: true, gfm: true });

// FLO-531: sanitize before the bubbles' dangerouslySetInnerHTML, matching what
// WebsiteAgentChat already does. Bot text is grounded in crawled customer
// content, so a prompt-injected <script>/onerror/`javascript:` is plausible —
// and the English-lexical leak guards that used to catch stray markup weaken in
// Hindi. Own instance (not the module singleton) so the anchor hook is ours.
const purify = DOMPurify(window);
purify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});
const renderMarkdown = (text: string): string =>
  purify.sanitize(marked.parse(text, { async: false }) as string, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'del', 'code', 'pre', 'ul', 'ol', 'li', 'a', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'hr'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });

export interface DemoMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
  isPartial?: boolean;
  type?: 'text' | 'form';
  formData?: {
    fields: { key: string; label: string; placeholder: string; type?: string; required?: boolean }[];
    submitLabel?: string;
  };
  formSubmitted?: boolean;
}

interface DemoViewProps {
  /**
   * Whether the overlay is running in demo mode. DemoView is currently only
   * mounted in demo mode (AgentUI renders it behind `if (demoMode)`), so this
   * is effectively always true today — it's threaded through explicitly so the
   * demo-specific defaults (e.g. starting with the chat sidebar collapsed) read
   * off a real prop and stay correct if DemoView is ever reused elsewhere.
   */
  demoMode?: boolean;
  /**
   * FLO-285: overlay placement when embedded on a host page.
   *   'docked'     — start minimized as a bottom-docked email-capture pill
   *                  (host page visible behind it), then go full-bleed once an
   *                  email is captured / the demo starts.
   *   'fullscreen' — open the intro form full-screen immediately (today's
   *                  behaviour). Defaults to 'fullscreen' here as the safe
   *                  historical fallback; the SDK passes 'docked' by default.
   */
  embedMode?: 'docked' | 'fullscreen';
  videoStream: MediaStream | null;
  /**
   * Avatar video track (demo mode, when the demo agent has its avatar
   * enabled). Rendered as a Google-Meet-style PiP at the top of the chat
   * sidebar. Null when no avatar is enabled or before the avatar track
   * arrives.
   */
  avatarStream?: MediaStream | null;
  /** Site idle loop + still, shown when the live avatar track is absent. */
  avatarIdleLoopUrl?: string;
  avatarPosterUrl?: string;
  botTranscript: string;
  botIsSpeaking: boolean;
  micEnabled: boolean;
  onToggleMute: () => void;
  onEndDemo: () => void;
  /** Configurable "close" action — full teardown that the host routes (e.g.
   *  back to the landing page). Fired by End Call and by the post-demo overlay's
   *  close (X) button. Required: the host MUST supply this — without it those
   *  controls would no-op and leave the prospect stuck on the wrap-up screen. */
  onCloseDemo: () => void;
  connected: boolean;
  agentState: string;
  messages?: DemoMessage[];
  currentTranscript?: string;
  currentRole?: 'user' | 'assistant';
  onSendText?: (text: string) => void;
  /**
   * FLO-307: deterministic opener chips — server-pushed one-tap ways into the
   * demo, rendered as a coral chip row above the text input. Present only on
   * turn 1 (the SDK clears them on the next user turn / demo end). Empty or
   * undefined → no chip row renders.
   */
  suggestedOptions?: string[];
  /** Tap handler for an opener chip — sends the chip text as a user turn. */
  onSelectOption?: (text: string) => void;
  /** Show the intro form for collecting prospect info */
  showIntroForm?: boolean;
  onIntroFormSubmit?: (data: Record<string, string>) => void;
  /** Error message from connection failure or bot crash */
  error?: string | null;
  /** Callback to retry the connection */
  onRetry?: () => void;
  /** Calendar link for booking a live demo with a human */
  calendarLink?: string;
  /** CDP Phase 1 (FLO-547): records a click and resolves its bounded, source-
   *  attributed destination before the first provider navigation. */
  onBookingCtaClick?: BookingIntentResolver;
  /** Resolve automatic post-demo calendar intent without inventing a click. */
  onResolveBookingIntent?: BookingIntentResolver;
  /** Provider iframe hint for immediate UX only; webhooks own conversion. */
  onBookingUxHint?: (hint: BookingUxHint) => void;
  /** Customer product name for branded loading screen + sidebar header */
  siteName?: string;
  /**
   * Customer product domain (e.g. "linear.app"). Displayed as the "Live demo ·
   * <domain>" caption in the header/summary. No longer used to derive a favicon
   * (FLO-296 self-hosts the favicon on the server — see siteFaviconUrl).
   */
  siteDomain?: string;
  /**
   * Customer product logo URL. If the dashboard has set one, it wins over the
   * favicon. Use a square SVG/PNG ≥ 64px for crisp render.
   */
  siteLogoUrl?: string;
  /**
   * FLO-296: self-hosted favicon URL (served by floe-api from R2, auto-fetched
   * from the customer's domain). The fallback below siteLogoUrl. Replaces the
   * old client-side Google s2/favicons derivation. Empty → letter-mark.
   */
  siteFaviconUrl?: string;
  /**
   * Brand accent as a #RRGGBB hex (FLO-64). Sets the overlay accent palette
   * (CTA buttons, active-state glows, accent surfaces) via CSS variables on
   * the root. Undefined/invalid → the Floe coral default (no visual change).
   */
  accentColor?: string;
  /**
   * Primary (booking/conversion) CTA label, e.g. "Talk to sales" (FLO-64).
   * Applies to the post-demo and end-call booking buttons. Defaults to
   * "Book a call" when unset.
   */
  ctaLabel?: string;
  /**
   * Bot audio output mute state. Owned by the SDK (the SDK mounts and
   * tracks the bot's <audio> elements, so it's the single source of
   * truth). DemoView is purely presentational for this — renders the
   * speaker button and threads `speakerMuted` to the avatar <video>.
   */
  speakerMuted?: boolean;
  onToggleSpeaker?: () => void;
  /**
   * Pre-supplied prospect email. When present, DemoIntroForm skips the
   * email stage (Stage 1) entirely and opens directly at Stage 2 with
   * the email pre-filled. The wrapper page is expected to have already
   * validated it — DemoView does NOT re-validate.
   *
   * Skips the ASK, not the cost gate: no session exists until the prospect
   * submits, however little is left to submit.
   */
  prefilledEmail?: string;
  /**
   * The rest of what the host page already knows about the prospect — a
   * `/d/[token]` demo link minted with prospect context carries name/company/
   * role alongside the email. These seed the intro form's fields so the
   * recipient never retypes details the link already had; with a name AND a
   * valid email the form has nothing left to ask and collapses to a one-tap
   * Start card (see planDemoIntro).
   */
  prefilledProspect?: DemoIntroPrefill;
  /**
   * The cost gate. The SDK triggers the deferred pipecat session start from
   * this and nothing else, so every call site must be inside a user gesture.
   *
   * Fires on the Stage 1 email advance (Stage 2 then runs in parallel with
   * pipecat warming up, so the bot is usually connected by "Start demo"), on
   * the docked pill's email submit, and on the final submit for the prefilled
   * paths that never passed through Stage 1. Idempotent downstream.
   */
  onProspectEmailCaptured?: (email: string) => void;
  /**
   * floe-api's obvious-fake check (test123@, throwaway domains, domains with
   * no mail). The form and the pill ask it while the visitor types and refuse
   * to start on a refused address. Absent ⇒ syntax check only.
   */
  checkProspectEmail?: EmailCheck;
  /**
   * The SAME cost gate, minus the email — used when `demoCapture` defers the
   * ask, so the Start tap has no address to carry. Still a gesture, and still
   * the only thing that spawns a session.
   */
  onStartDemoWithoutEmail?: () => void;
  /**
   * Resolved identity-capture policy (site default ← per-link override). Two
   * jobs: it decides whether the intro gate is a FORM or a fieldless Start card
   * (`captureIsDeferred`), and it describes the conversational fields.
   * Undefined → today's form, which is also what an older floe-api produces.
   */
  demoCapture?: DemoCapturePolicy;
  identityAsk?: IdentityAskState | null;
  onIdentityConfirm?: (value: string) => boolean | void;
  onIdentityReject?: () => void;
  /** Whether the demo ended normally (vs error/disconnect) */
  demoEnded?: boolean;
  /** Post-demo summary text from the AI */
  demoSummary?: string | null;
  /** Features the bot showed during the demo (from end_demo tool call). */
  featuresShown?: string[];
  /** Suggested next step (from end_demo tool call). */
  nextStep?: string | null;
  languageOptions?: LanguageOption[];
  selectedLanguage?: SupportedLanguage;
  pendingLanguageSwitch?: PendingLanguageSwitch | null;
  languageSwitchError?: string | null;
  languageSwitchDisabled?: boolean;
  onLanguageChange?: (language: SupportedLanguage) => void;
}

// ──────────────────────────────────────
// Design tokens — mirrors floe-site/src/app/globals.css.
//
// FONT_SANS:    Plus Jakarta Sans, the body face used across floe.so.
//               --font-jakarta is exposed on <html> when this SDK loads
//               on a page that booted Next/Font. Falls back to a hosted
//               import (literal "Plus Jakarta Sans") or system sans.
// FONT_DISPLAY: DM Serif Display, the editorial face for big headlines.
//               Same fallback chain.
//
// Noto Sans Devanagari sits after the Latin faces in BOTH stacks (FLO-531):
// neither Jakarta nor DM Serif has Devanagari glyphs, so a Hindi session would
// otherwise fall to a per-OS face — and in FONT_DISPLAY that meant Georgia, a
// serif with no Devanagari either. Latin still resolves from the Latin faces;
// only Devanagari codepoints reach Noto. The SDK injects it (see initReactUI).
//
// The color tokens are the warm coffee/cream palette the marketing site
// uses — warmer than the prior pure-gray/pure-white the SDK form was on,
// so the form reads as "Floe" instead of "generic dark modal."
// ──────────────────────────────────────
const FONT_SANS =
  'var(--font-jakarta), "Plus Jakarta Sans", "Noto Sans Devanagari", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
const FONT_DISPLAY =
  'var(--font-dm-serif), "DM Serif Display", "Noto Sans Devanagari", Georgia, "Times New Roman", serif';
const COLOR_BG = '#0C0A09';
const COLOR_BG_ELEVATED = '#141211';
// COLOR_BG_CARD (#1A1816) reserved for nested-card surfaces if we add
// any. Not exported because it's not used yet — keeping the constant
// here documents the palette completeness.
// const COLOR_BG_CARD = '#1A1816';
const COLOR_TEXT = '#F5F0EB';
const COLOR_TEXT_MUTED = '#C4BAB0';
const COLOR_TEXT_DIM = '#8A827A';
const COLOR_BORDER = '#2A2523';
const COLOR_BORDER_LIGHT = '#3A3530';
// Accent palette (FLO-64). Each reads a CSS variable set on the DemoView root
// from the per-site brand accent (see deriveAccentVars), falling back to the
// hand-tuned Floe coral when no accent is configured — so an unbranded demo
// renders byte-identically to before. The alpha variants exist because several
// call sites used to append an alpha byte to the hex (`${COLOR_ACCENT_STRONG}`),
// which is invalid against a var(); each gets its own variable instead.
const COLOR_ACCENT = 'var(--floe-accent, #FF5C35)';
const COLOR_ACCENT_STRONG = 'var(--floe-accent-strong, #FF5C35E6)'; // 90% — gradient 2nd stop
const COLOR_ACCENT_HOVER = 'var(--floe-accent-hover, #FF7A5A)';
const COLOR_ACCENT_GLOW = 'var(--floe-accent-glow, rgba(255, 92, 53, 0.35))';
const COLOR_ACCENT_MUTED = 'var(--floe-accent-muted, rgba(255, 92, 53, 0.12))';
const COLOR_ACCENT_20 = 'var(--floe-accent-20, rgba(255, 92, 53, 0.2))';  // was `${COLOR_ACCENT_20}`
const COLOR_ACCENT_15 = 'var(--floe-accent-15, rgba(255, 92, 53, 0.15))'; // was `${COLOR_ACCENT_15}`
const COLOR_ACCENT_40 = 'var(--floe-accent-40, rgba(255, 92, 53, 0.4))';  // was `${COLOR_ACCENT_40}`
const COLOR_ACCENT_08 = 'var(--floe-accent-08, rgba(255, 92, 53, 0.08))'; // 8% — was `${ACCENT}14`

// Cream-tinted overlays for surfaces over the warm dark backdrop. Tinting
// with the cream text color (#F5F0EB) instead of pure white keeps things
// in the same warm temperature family as the rest of floe-site.
const SURFACE_HOVER = 'rgba(245,240,235,0.06)';
const SURFACE_FAINT = 'rgba(245,240,235,0.03)';

// FONT was an alias for backward-compat during the migration; all call
// sites in this file now use FONT_SANS directly.

// State indicator colors — chosen to harmonize with the warm coffee
// palette rather than the previous cool greens/blues/yellows. The
// "speaking" + "guiding" states use coral (the brand accent) since
// those are the moments the agent is actively producing value; the
// other states use warmer ambers and creams.
const stateColor = (s: string, conn: boolean, speaking: boolean) => {
  if (!conn) return '#E8A857';                 // warm amber for connecting
  if (s === 'listening') return COLOR_TEXT_MUTED; // calm cream
  if (s === 'thinking') return '#E8A857';      // warm amber for processing
  if (s === 'speaking' || speaking) return COLOR_ACCENT; // coral when the bot is talking
  if (s === 'guiding') return COLOR_ACCENT;    // coral when actively driving the UI
  return COLOR_TEXT_DIM;                       // idle / ready
};

const stateLabel = (s: string, conn: boolean, speaking: boolean) => {
  if (!conn) return getString('demo.state.connecting');
  if (s === 'listening') return getString('demo.state.listening');
  if (s === 'thinking') return getString('demo.state.thinking');
  if (s === 'speaking' || speaking) return getString('demo.state.speaking');
  if (s === 'guiding') return getString('demo.state.guiding');
  return getString('demo.state.ready');
};

/**
 * Three-tier logo resolution:
 *   1. Explicit siteLogoUrl (highest fidelity, dashboard-set / auto-fetched logo).
 *   2. Self-hosted favicon (FLO-296) — faviconUrl, served by floe-api from R2
 *      after fetching the customer's own favicon at site creation. Replaces the
 *      former live Google s2/favicons hot-link (removed: third-party dependency
 *      on every render + a privacy leak of the prospect's domain to Google).
 *   3. Letter-mark in a coral-tinted circle (last-resort placeholder).
 *
 * The letter-mark fallback ensures we never render a broken-image icon.
 * `onError` on the <img> triggers it if the stored image URL ever fails to load.
 */
const CompanyLogo: React.FC<{
  name: string;
  logoUrl: string;
  faviconUrl: string;
  size: number;
}> = ({ name, logoUrl, faviconUrl, size }) => {
  // First letter of the product name, uppercased. Falls back to "•" if name
  // is empty so the placeholder still renders something centered.
  const letter = (name || '').trim().slice(0, 1).toUpperCase() || '•';
  // Ordered image candidates: explicit logo first, then the self-hosted
  // favicon. onError advances to the NEXT source (via srcIndex) so a failed
  // logoUrl still tries the favicon before we drop to the letter-mark —
  // preserving the logoUrl → faviconUrl → letter-mark precedence. `key={src}`
  // forces a fresh <img> per source so onError reliably fires for each.
  const sources = [logoUrl, faviconUrl].filter(Boolean);
  const [srcIndex, setSrcIndex] = useState(0);
  const src = sources[srcIndex];

  const baseStyle: React.CSSProperties = {
    width: size, height: size, borderRadius: Math.max(6, Math.round(size * 0.22)),
    flexShrink: 0, overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  if (src) {
    return (
      <div style={{
        ...baseStyle,
        background: SURFACE_HOVER,
        border: `1px solid ${COLOR_BORDER}`,
      }}>
        <img
          key={src}
          src={src}
          alt={`${name || 'Product'} logo`}
          width={size}
          height={size}
          onError={() => setSrcIndex((i) => i + 1)}
          style={{
            width: '100%', height: '100%',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </div>
    );
  }

  // Letter-mark fallback. Coral tint stays in the Floe palette so even when
  // we don't know the brand, the chrome feels intentional.
  return (
    <div style={{
      ...baseStyle,
      background: COLOR_ACCENT_MUTED,
      border: `1px solid ${COLOR_ACCENT_20}`,
      color: COLOR_ACCENT,
      fontFamily: FONT_DISPLAY,
      fontSize: Math.round(size * 0.5),
      fontWeight: 400,
      letterSpacing: '-0.02em',
      lineHeight: 1,
    }}>
      {letter}
    </div>
  );
};

/**
 * Equal-sized icon-only toggle for the footer controls row (mic, speaker,
 * end demo). One small primitive instead of three bespoke buttons —
 * keeps the row visually rhythmic.
 *
 * tone="default": cream icon on transparent bg, hover-faint.
 * tone="danger":  coral icon on a coral-muted bg, hover-brighter. Used
 *                 for "End demo" since it's the destructive action.
 *
 * active=false flips default-toned buttons to a coral-muted bg so the
 * "muted" state reads as on-fire (the SOMETHING-is-on-here visual
 * grammar people already learn from Zoom/Meet mute toggles).
 */
const IconToggle: React.FC<{
  active: boolean;
  onClick: () => void;
  title: string;
  ariaLabel: string;
  tone?: 'default' | 'danger';
  children: React.ReactNode;
}> = ({ active, onClick, title, ariaLabel, tone = 'default', children }) => {
  const isDanger = tone === 'danger';
  const offState = !active && !isDanger;
  const baseBg = isDanger
    ? COLOR_ACCENT_MUTED
    : offState
      ? COLOR_ACCENT_MUTED
      : SURFACE_FAINT;
  const baseColor = isDanger
    ? COLOR_ACCENT
    : offState
      ? COLOR_ACCENT
      : COLOR_TEXT_MUTED;
  const hoverBg = isDanger
    ? `${COLOR_ACCENT_15}`
    : offState
      ? `${COLOR_ACCENT_15}`
      : SURFACE_HOVER;
  const hoverColor = isDanger
    ? COLOR_ACCENT_HOVER
    : offState
      ? COLOR_ACCENT_HOVER
      : COLOR_TEXT;
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
      style={{
        width: 36, height: 36, borderRadius: 10, border: 'none',
        background: baseBg,
        color: baseColor,
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        transition: 'background 0.15s, color 0.15s, transform 0.15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = hoverBg;
        e.currentTarget.style.color = hoverColor;
        e.currentTarget.style.transform = 'scale(1.06)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = baseBg;
        e.currentTarget.style.color = baseColor;
        e.currentTarget.style.transform = 'scale(1)';
      }}
    >
      {children}
    </button>
  );
};

/**
 * Screencast <video> — isolated + memoized so the streaming transcript
 * (botTranscript / currentTranscript update many times per second while the
 * agent is speaking) can't trigger React to touch this element or force a
 * repaint of the decoded video frame. On a mid-range phone that re-render
 * churn was stealing main-thread time from the compositor and dropping video
 * frames (FLO-110 — "choppy on mobile").
 *
 * Two independent guards:
 *   1. React.memo + a stable `onPlaying` callback → this subtree only
 *      re-renders when `videoReady` flips, NOT on every transcript token, so
 *      the inline style object isn't recreated and re-diffed onto the element
 *      every frame.
 *   2. transform: translateZ(0) + willChange + contain: 'paint' → promotes the
 *      video onto its own GPU compositor layer. Repaints of the sibling
 *      caption / chat region then re-composite (cheap, GPU) instead of
 *      re-rasterizing the video texture.
 *
 * objectFit stays `contain` (NOT `cover`, which FLO-110 floated): a product
 * screencast has to show the whole frame — `cover` would crop the very UI the
 * demo is walking the prospect through. The choppiness was main-thread /
 * compositor contention, not the letterbox scale, so layer isolation is the
 * actual fix.
 */
const ScreencastVideo = React.memo<{
  videoRef: React.RefObject<HTMLVideoElement>;
  videoReady: boolean;
  onPlaying: () => void;
}>(({ videoRef, videoReady, onPlaying }) => (
  <video
    ref={videoRef}
    style={{
      width: '100%', height: '100%', objectFit: 'contain',
      display: 'block', background: COLOR_BG,
      opacity: videoReady ? 1 : 0,
      transition: 'opacity 0.4s ease',
      // Keep the decoded video on its own GPU layer (see component doc).
      transform: 'translateZ(0)',
      willChange: 'transform',
      backfaceVisibility: 'hidden',
      contain: 'paint',
    }}
    autoPlay playsInline muted
    onPlaying={onPlaying}
  />
));
ScreencastVideo.displayName = 'ScreencastVideo';

export const DemoView: React.FC<DemoViewProps> = ({
  demoMode = false,
  embedMode = 'fullscreen',
  videoStream, avatarStream, avatarIdleLoopUrl, avatarPosterUrl,
  botTranscript, botIsSpeaking, micEnabled,
  onToggleMute, onEndDemo, onCloseDemo, connected, agentState,
  messages = [], currentTranscript = '', currentRole, onSendText,
  suggestedOptions, onSelectOption,
  showIntroForm, onIntroFormSubmit,
  error, onRetry, calendarLink, onBookingCtaClick, onResolveBookingIntent, onBookingUxHint,
  siteName, siteDomain, siteLogoUrl, siteFaviconUrl,
  accentColor, ctaLabel,
  speakerMuted = false, onToggleSpeaker,
  prefilledEmail, prefilledProspect, onProspectEmailCaptured, checkProspectEmail,
  onStartDemoWithoutEmail, demoCapture,
  identityAsk, onIdentityConfirm, onIdentityReject,
  demoEnded, demoSummary, featuresShown, nextStep,
  languageOptions = [], selectedLanguage = 'en', pendingLanguageSwitch,
  languageSwitchError, languageSwitchDisabled = false, onLanguageChange,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const bookingFrameRef = useRef<HTMLIFrameElement>(null);
  const bookingHintSentForUrl = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [textInput, setTextInput] = useState('');
  const controlInputRef = useRef<HTMLInputElement>(null);
  const consumeIdentityDraft = useIdentityDraft(identityAsk, textInput, setTextInput, () => {
    requestAnimationFrame(() => { const input = textInputRef.current?.offsetParent ? textInputRef.current : controlInputRef.current; input?.focus(); input?.select(); });
  });
  const activeIdentity = identityAsk?.status !== 'resolved' ? identityAsk : null;
  const confirmMode = !!activeIdentity && classifyComposerSubmit(activeIdentity.field, textInput) === 'confirm';
  const identityInputProps = activeIdentity ? { inputMode: activeIdentity.field === 'phone' ? 'tel' as const : activeIdentity.field === 'email' ? 'email' as const : 'text' as const, autoComplete: activeIdentity.field === 'phone' ? 'tel' : activeIdentity.field === 'email' ? 'email' : 'off' } : {};

  // Reactive mobile check — the shared responsive foundation. Consumed LIVE
  // (not just at mount): it seeds the sidebar's initial default, and drives the
  // open-sidebar layout (full-screen overlay vs. side panel), the post-demo
  // auto-open, the stacked control bar, and the caption clamp — all of which
  // re-flow when the viewport crosses the breakpoint on resize/rotate.
  const isMobile = useIsMobile();
  // Start the chat sidebar COLLAPSED on mobile only: the phone has no room for
  // a side panel beside the video, so the demo opens on the full-bleed product
  // video with chat one tap away via the floating "open chat" button. On
  // desktop there's room for the panel, so the sidebar opens (its prior
  // behaviour). This is a default only (lazy initializer) — it sets the entry
  // state without fighting the user if they later toggle the sidebar and resize.
  const [sidebarOpen, setSidebarOpen] = useState(() => !isMobile);
  const [introFormSubmitted, setIntroFormSubmitted] = useState(false);
  // Has the cost gate fired — i.e. is a pipecat session actually on its way?
  // Tracked because it is no longer inferable from `prefilledEmail`: a
  // prefilled email means "we can skip asking", not "a session is starting"
  // (nothing starts at init any more). Drives the status line under the form,
  // which would otherwise promise "Booting the agent…" over a dead connection.
  const [sessionStarting, setSessionStarting] = useState(false);
  const captureProspectEmail = useCallback(
    (email: string) => {
      setSessionStarting(true);
      onProspectEmailCaptured?.(email);
    },
    [onProspectEmailCaptured],
  );
  // Is the identity ask deferred into the demo? Then the intro surface has
  // nothing to collect and every entry point (pill + form) becomes a bare
  // "Start demo" — which is still the cost gate and still the mic gesture.
  const captureDeferred = captureIsDeferred(demoCapture);
  // The gate for the deferred path. Same setSessionStarting bookkeeping as
  // captureProspectEmail so the status line under the card is honest.
  const startWithoutEmail = useCallback(() => {
    setSessionStarting(true);
    onStartDemoWithoutEmail?.();
  }, [onStartDemoWithoutEmail]);
  // FLO-285: email captured from the docked entry pill. Non-null once the
  // prospect submits the pill → the overlay leaves the docked state and goes
  // full-bleed. Also handed to the full-screen intro form as prefilledEmail so
  // it opens at Stage 2 (name) instead of re-asking for the email.
  const [pillEmail, setPillEmail] = useState<string | null>(null);
  // The fieldless pill's Start has fired. Under a deferred policy there is no
  // email to capture, so `pillEmail` never moves — without this the pill would
  // boot a paid session and then keep rendering over the host page forever.
  const [pillStarted, setPillStarted] = useState(false);
  const [wasConnected, setWasConnected] = useState(false);
  const [userEndedDemo, setUserEndedDemo] = useState(false);
  // End-call confirmation modal — gates the destructive end-call action.
  // Clicking the End demo button opens this confirmation so the prospect can
  // pick "Book Demo" (ends into wrap-up and lands them on the Cal/Calendly
  // calendar in place) or "End Call" (the close action → host routes to the
  // landing page). Escape / clicking the backdrop dismisses (keep exploring).
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const endConfirmRef = useRef<HTMLDivElement>(null);
  const [authTimedOut, setAuthTimedOut] = useState(false);
  // Speaker mute lives in the SDK now (single source of truth — see
  // toggleSpeakerMute in onboarding-sdk.ts). `speakerMuted` is a
  // controlled prop; this view only needs to apply it to the avatar
  // <video> below (the SDK already handles its own <audio> elements).
  // Intermediate state: true after the prospect has been on the loading screen for 30s
  // without a video stream arriving. Drives a friendlier "this takes a moment" message
  // so they don't think the demo is broken during the browser-auth + captcha window.
  const [slowConnect, setSlowConnect] = useState(false);

  // FLO-285: docked entry state — the minimized bottom pill is showing and the
  // demo hasn't gone full-bleed yet. True only in 'docked' mode, while the intro
  // form is still pending, and before an email has been captured (here via the
  // pill, or up-front via the wrapper's prefilledEmail). Gates the scroll-lock
  // and the auth-timeout below (neither should run while we're just showing a
  // pill over the host page), and drives the early return further down.
  const dockedEntry = shouldShowDockedPill({
    demoMode,
    embedMode,
    showIntroForm: !!showIntroForm,
    introFormSubmitted,
    hasCapturedEmail: !!pillEmail,
    hasPrefilledEmail: !!prefilledEmail,
    hasStarted: pillStarted,
  });

  // Track if we were ever connected (to detect unexpected disconnects)
  useEffect(() => {
    if (connected) setWasConnected(true);
  }, [connected]);

  // Lock background scroll while the full-screen demo overlay is mounted.
  // The demo is a fixed full-viewport takeover; leaving the host page
  // scrollable lets a stray mobile swipe shift the visual viewport so the
  // top-right "open chat" toggle slides out of reach — the menu "stops
  // opening" once you've scrolled (FLO-108). Demo mode only: the onboarding
  // overlay is a transparent guidance layer and must keep the page scrollable.
  // FLO-285: skip while docked — the pill is a small overlay and the host
  // marketing page must stay scrollable behind it. The lock kicks in on the
  // transition to full-bleed (dockedEntry flips false once an email lands).
  useEffect(() => {
    if (!demoMode || dockedEntry) return;
    return lockBodyScroll();
  }, [demoMode, dockedEntry]);

  // Auth timeout: if not connected after 90 seconds, show error.
  // Klaar / Cal.com first-time login (auth + captcha) takes 40-70s; the previous
  // 45s cutoff was firing while the bot was still authenticating, causing a
  // false "Demo session ended" error before the demo actually started. Bumped
  // to 90s with the slowConnect intermediate state at 30s as a visible signal.
  // Only runs once a session is actually on its way (`sessionStarting`). This
  // measures how long a CONNECT is taking, so starting it before anything is
  // connecting counts the prospect's own reading and typing time against the
  // bot, and fires "Demo session ended" at someone who never started one.
  //
  // FLO-285 established this for the docked pill, where no session exists until
  // the pill's email is submitted. The same hole was open on both full-screen
  // paths, which skip the pill entirely: on a prefilled link the prospect can
  // sit on the one-tap Start card, and on a curated demo they can sit on the
  // Stage 1 email field, for 90s and get a false timeout on a demo that was
  // never started. `dockedEntry` is now subsumed by `sessionStarting` but kept
  // for clarity about the pill case.
  useEffect(() => {
    if (
      connected || wasConnected || error || demoEnded || authTimedOut ||
      dockedEntry || !sessionStarting
    ) return;
    const timer = setTimeout(() => setAuthTimedOut(true), 90000);
    return () => clearTimeout(timer);
  }, [connected, wasConnected, error, demoEnded, authTimedOut, dockedEntry, sessionStarting]);

  // SlowConnect: after 30s of being on the loading screen without video, show
  // the "first-session auth can take a moment" message. Resets when video arrives
  // or an error/end state takes over. Uses raw state instead of derived flags
  // because those are declared after this effect (temporal dead zone).
  useEffect(() => {
    if (videoReady || demoEnded || authTimedOut) {
      setSlowConnect(false);
      return;
    }
    if (!introFormSubmitted) return;
    const slowTimer = setTimeout(() => setSlowConnect(true), 30000);
    return () => clearTimeout(slowTimer);
  }, [introFormSubmitted, videoReady, demoEnded, authTimedOut]);

  // Focus trap for the end-call confirmation modal. Without this, Tab can
  // move focus to controls behind the dialog (mic toggle, transcript, etc.),
  // which breaks the modal's accessibility contract. On open: focus End Call
  // (the last focusable — the safer default, since Book Demo navigates out to
  // the calendar), then cycle Tab/Shift+Tab between the first and last
  // focusables so focus can't escape the modal.
  useEffect(() => {
    if (!showEndConfirm) return;
    const card = endConfirmRef.current;
    if (!card) return;
    const focusables = card.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    last.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showEndConfirm]);

  // Per-site brand accent (FLO-64) → CSS variables spread onto the root div
  // below; the COLOR_ACCENT* constants read them. Undefined for no/invalid
  // accent, leaving the var() fallbacks (Floe coral) in place — so an
  // unbranded demo is visually unchanged. Pure + keyed on the prop.
  const accentVarStyle = useMemo(
    () => deriveAccentVars(accentColor) as React.CSSProperties | undefined,
    [accentColor],
  );

  // ONE resolution of the booking URL for every surface on this view (review
  // follow-up). It used to be resolved only inside the post-demo branch, so the
  // three OTHER booking call sites gated on raw `calendarLink` truthiness and
  // disagreed with it in both directions:
  //
  //   - the error-state anchor put the raw value straight into an `href`, with
  //     no scheme guard at all — the one place a `javascript:` booking URL was
  //     script execution on our own page (the post-demo branch had guarded this
  //     since it was written; this path never did);
  //   - the wrap-up modal offered "Book Demo" for any non-empty string, so an
  //     `http://` or malformed URL ended the demo onto the empty "Ready to talk
  //     next steps?" stub — the exact silent dead-end the modal's own comment
  //     says the gate exists to prevent.
  //
  // `bookable` is now the single gate and `bookingHref` the single href.
  const calendar = useMemo(() => resolveCalendarEmbed(calendarLink), [calendarLink]);
  const bookingHref = calendar.fallbackHref;
  const bookable = bookingHref !== null;
  // Show post-demo success screen (normal end, not error).
  const showPostDemo = !!demoEnded && !error;
  const [postDemoCalendarLink, setPostDemoCalendarLink] = useState<string | null>(null);
  const [postDemoBookingPending, setPostDemoBookingPending] = useState(false);
  const postDemoCalendar = useMemo(
    () => resolveCalendarEmbed(postDemoCalendarLink),
    [postDemoCalendarLink],
  );

  // Natural demo completion is itself booking intent: the calendar is the next
  // screen. Resolve the bounded source correlation before mounting the provider
  // iframe for the first time; failure still lands on the configured base URL.
  useEffect(() => {
    if (!showPostDemo || !bookingHref) {
      setPostDemoBookingPending(false);
      setPostDemoCalendarLink(null);
      return;
    }
    let current = true;
    setPostDemoBookingPending(true);
    setPostDemoCalendarLink(null);
    void resolveBookingIntentDestination(
      bookingHref,
      onResolveBookingIntent,
    ).then((destination) => {
      if (!current) return;
      setPostDemoCalendarLink(destination || bookingHref);
      setPostDemoBookingPending(false);
    });
    return () => {
      current = false;
    };
  }, [showPostDemo, bookingHref, onResolveBookingIntent]);

  useEffect(() => {
    if (!postDemoCalendar.embedSrc || !onBookingUxHint) return;
    const onMessage = (event: MessageEvent) => {
      const hint = bookingUxHintFromMessage({
        event,
        iframe: bookingFrameRef.current,
        calendarUrl: postDemoCalendar.embedSrc,
      });
      if (!hint || bookingHintSentForUrl.current === postDemoCalendar.embedSrc) return;
      bookingHintSentForUrl.current = postDemoCalendar.embedSrc;
      onBookingUxHint(hint);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [postDemoCalendar.embedSrc, onBookingUxHint]);

  const handleBookingLinkClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      if (!onBookingCtaClick) return;
      if (openResolvedBookingWindow(href, onBookingCtaClick)) {
        event.preventDefault();
      }
    },
    [onBookingCtaClick],
  );

  // Resolved primary booking CTA copy (FLO-64). Each call site keeps its own
  // default when unset, so an unbranded demo reads exactly as before; a
  // configured label unifies them to the customer's wording.
  const localizedCtaLabel = resolveUntaggedOperatorCopy(ctaLabel?.trim(), selectedLanguage);
  const bookCallLabel = localizedCtaLabel || getString('demo.bookCall');
  const bookDemoLabel = localizedCtaLabel || getString('demo.bookDemo');
  const bookLiveDemoLabel = localizedCtaLabel || getString('demo.bookLiveDemo');

  // Post-demo wrap-up shows the recap sidebar — the summary and the close
  // affordances (header X + bottom "Close demo" button) live there. On desktop
  // force it OPEN so a prospect who minimized mid-demo still lands on a
  // labelled way out beside the calendar. On mobile the sidebar is a
  // full-screen overlay that would bury the booking calendar (the post-demo
  // CTA), so force it CLOSED — even if the prospect had it open mid-demo — so
  // they land on the full-bleed calendar; the recap is one tap away via the
  // open-chat toggle. Setting the sidebar to the helper's target state (open on
  // desktop, closed on mobile) handles both. Re-runs on a breakpoint cross.
  useEffect(() => {
    if (showPostDemo) setSidebarOpen(shouldAutoOpenPostDemoSidebar(showPostDemo, isMobile));
  }, [showPostDemo, isMobile]);

  // Show error: explicit error prop, auth timeout, OR unexpected disconnect (not user-initiated)
  const showError = !showPostDemo && (!!error || authTimedOut || (wasConnected && !connected && !userEndedDemo));
  const errorMessage = authTimedOut
    ? getNamedString('demo.errorUnreachable', siteName, 'the product', 'demo.error')
    : error || getString('demo.error');

  useEffect(() => {
    if (videoRef.current && videoStream) {
      videoRef.current.srcObject = videoStream;
      videoRef.current.play().catch(() => {});
    }
  }, [videoStream]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentTranscript]);

  const handleTextSubmit = () => {
    if (!textInput.trim() || !onSendText || !connected) return;
    if (confirmMode && onIdentityConfirm) {
      if (onIdentityConfirm(textInput.trim()) === false) return;
      consumeIdentityDraft();
    }
    else onSendText(textInput.trim());
    setTextInput('');
    textInputRef.current?.focus();
  };

  // Stable callback so the memoized <ScreencastVideo> isn't re-rendered on
  // every transcript token (a fresh inline closure would defeat React.memo).
  const handleVideoPlaying = useCallback(() => setVideoReady(true), []);

  const sc = stateColor(agentState, connected, botIsSpeaking);
  const sl = stateLabel(agentState, connected, botIsSpeaking);

  const displayMessages = useMemo(() => {
    const msgs = [...messages];
    if (currentTranscript && currentRole) {
      msgs.push({
        id: '__streaming__',
        role: currentRole,
        content: currentTranscript,
        timestamp: Date.now(),
        isPartial: true,
      });
    }
    return msgs;
  }, [messages, currentTranscript, currentRole]);

  // Gate the demo behind the intro form: form must be submitted before video appears
  const formPending = showIntroForm && !introFormSubmitted;

  // FLO-285: docked entry — render ONLY the minimized bottom pill, leaving the
  // host marketing page visible + interactive behind it (no full-viewport
  // takeover, no scroll-lock). Submitting the pill's email captures it and fires
  // the cost-gate; the next render flips `dockedEntry` false and the full-bleed
  // demo below takes over, with the intro form opening at Stage 2 (name) since
  // the captured email is passed through as prefilledEmail.
  if (dockedEntry) {
    return (
      <DemoDockedPill
        siteName={siteName}
        siteLogoUrl={siteLogoUrl}
        accentVarStyle={accentVarStyle}
        // Deferred capture ⇒ no email field: the ask happens inside the demo,
        // so the pill is a plain "Start demo" button. Its tap is still the cost
        // gate, so the pill keeps its whole reason to exist.
        collectEmail={!captureDeferred}
        onStart={() => {
          // The fieldless pill tap is the WHOLE intro, so it has to do
          // everything the full-screen Start card does — otherwise the demo
          // boots and then sits behind a second, identical Start card.
          //
          //  1. leave the docked state (nothing else moves under a deferred
          //     policy — there is no email capture to do it for us),
          //  2. fire the cost gate, and
          //  3. submit the intro, which is what sends `prospect_prefill`.
          //
          // (3) is not bookkeeping: that message is the session's KICKOFF —
          // skipping it boots a paid session whose agent never greets anyone.
          setPillStarted(true);
          setIntroFormSubmitted(true);
          startWithoutEmail();
          // Whatever the host already knew, in the shape the form would have
          // submitted. Usually {} on a docked marketing embed.
          onIntroFormSubmit?.(
            planDemoIntro({ ...prefilledProspect, email: prefilledEmail }, demoCapture)
              .values,
          );
        }}
        onSubmitEmail={(email) => {
          setPillEmail(email);
          captureProspectEmail(email);
        }}
        checkEmail={checkProspectEmail}
        languageOptions={languageOptions}
        selectedLanguage={selectedLanguage}
        pendingLanguageSwitch={pendingLanguageSwitch}
        languageSwitchError={languageSwitchError}
        languageSwitchDisabled={languageSwitchDisabled}
        onLanguageChange={onLanguageChange}
      />
    );
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, display: 'flex',
      backgroundColor: COLOR_BG, zIndex: 2147483646,
      fontFamily: FONT_SANS, color: COLOR_TEXT, pointerEvents: 'auto',
      // Brand accent palette (FLO-64). Inherited by every descendant, incl.
      // the post-demo + end-call surfaces and the injected <style> block.
      ...accentVarStyle,
    }}>

      {/* ════════════════════════════════════════
          FULL-SCREEN INTRO FORM (gates the demo)
          ════════════════════════════════════════ */}
      {formPending && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '40px 24px',
          // Warm near-black — matches floe-site --t-bg. Was a cooler
          // #0a0a0c / #0e0e10 that didn't match the marketing palette.
          background: COLOR_BG,
          overflow: 'hidden',
        }}>
          {onLanguageChange && (
            <div style={{ position: 'absolute', top: 22, right: 24, zIndex: 2 }}>
              <LanguageSelector
                languages={languageOptions}
                value={selectedLanguage}
                pending={pendingLanguageSwitch}
                error={languageSwitchError}
                disabled={languageSwitchDisabled}
                appearance="dark"
                onChange={onLanguageChange}
              />
            </div>
          )}
          {/* Soft ambient glow behind the form — replaces the visible
              card border the form used to have. Grounds the content
              without putting a box around it. */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: '20%', left: '50%',
              transform: 'translateX(-50%)',
              width: 700, height: 700,
              background:
                'radial-gradient(circle, rgba(255,92,53,0.12) 0%, transparent 65%)',
              filter: 'blur(80px)',
              pointerEvents: 'none',
            }}
          />
          <div style={{
            position: 'relative',
            width: 480, maxWidth: '100%',
          }}>
            <DemoIntroForm
              // FLO-285: in docked mode the pill already captured (and fired the
              // cost-gate for) the email, so open at Stage 2 with it prefilled
              // rather than re-asking. Falls back to the wrapper's prefilledEmail.
              prefilledEmail={pillEmail ?? prefilledEmail}
              // Name/company/role from a demo link minted with prospect context.
              // Seeds the fields so nothing is retyped; with a name present the
              // form renders as the one-tap Start card instead.
              prefilledProspect={prefilledProspect}
              // Deferred ⇒ the form collapses to a fieldless Start card whose
              // tap fires `onStart` instead of the email gate. planDemoIntro
              // handles the collapse; the form needs the policy to know which
              // gate to call and not to demand an email it never asked for.
              capture={demoCapture}
              onStart={startWithoutEmail}
              onEmailCaptured={captureProspectEmail}
              checkEmail={checkProspectEmail}
              // The host page or demo link supplied this one: not the
              // visitor's typing, so it is not second-guessed.
              trustedEmail={prefilledEmail}
              onSubmit={(data) => {
                setIntroFormSubmitted(true);
                onIntroFormSubmit?.(data);
              }}
            />
            <p style={{
              color: COLOR_TEXT_DIM, fontSize: 12, textAlign: 'left',
              marginTop: 28, lineHeight: 1.5,
              fontFamily: FONT_SANS,
              opacity: 0.6,
            }}>
              {/* connected only flips after the deferred pipecat session is up,
                  which only happens once the cost gate has fired. Key on
                  sessionStarting, NOT on prefilledEmail: a prefilled email
                  means the form has less to ask, not that anything is booting
                  — claiming otherwise would show "Booting the agent…" over a
                  session nobody has started yet. */}
              {connected
                ? 'Agent is ready when you are.'
                : sessionStarting || introFormSubmitted
                  ? 'Booting the agent…'
                  : 'The agent will start once you continue.'}
            </p>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════
          LEFT: Video area
          ════════════════════════════════════════ */}
      {/* Outer pane carries the breathing room around the framed video.
          - When the sidebar is open, equal 20px padding floats the frame
            inside the canvas.
          - When the sidebar is closed, we widen top + bottom so the
            top-right "open chat" icon and the bottom control bar don't
            overlap the video. The icon sits in the top padding zone; the
            controls live in the bottom one. */}
      <div style={{
        flex: 1, minWidth: 0,
        padding: sidebarOpen ? 20 : '64px 20px 112px 20px',
        background: COLOR_BG,
        display: 'flex',
        transition: 'padding 0.25s cubic-bezier(0.16,1,0.3,1)',
      }}>
        <div style={{
          flex: 1, position: 'relative', overflow: 'hidden',
          borderRadius: 14,
          background: '#0A0807',
          border: `1px solid ${COLOR_BORDER}`,
          boxShadow: '0 18px 48px -20px rgba(0,0,0,0.6)',
        }}>
        {/* Error / disconnected state */}
        {showError && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(12, 10, 9, 0.92)',
            backdropFilter: 'blur(8px)',
            padding: '40px 24px',
          }}>
            {/* Soft coral glow behind the error card — same grounding
                pattern as the intro form's overlay. */}
            <div aria-hidden style={{
              position: 'absolute', top: '30%', left: '50%',
              transform: 'translateX(-50%)',
              width: 600, height: 600,
              background: 'radial-gradient(circle, rgba(255,92,53,0.08) 0%, transparent 65%)',
              filter: 'blur(80px)',
              pointerEvents: 'none',
            }} />
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* Error icon — coral-tinted instead of red so it stays in
                  the brand palette. */}
              <div style={{
                width: 52, height: 52, borderRadius: '50%',
                background: COLOR_ACCENT_MUTED,
                border: `1px solid ${COLOR_ACCENT_20}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 22,
              }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={COLOR_ACCENT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <p style={{
                fontFamily: FONT_DISPLAY,
                color: COLOR_TEXT, fontSize: 28, fontWeight: 400,
                letterSpacing: '-0.01em', lineHeight: 1.15,
                margin: '0 0 10px', textAlign: 'center',
              }}>
                {getString('demo.ended')}
              </p>
              <p style={{
                fontFamily: FONT_SANS,
                color: COLOR_TEXT_MUTED, fontSize: 14, lineHeight: 1.55,
                margin: '0 0 32px', maxWidth: 380, textAlign: 'center',
                opacity: 0.85,
              }}>
                {errorMessage}
              </p>
              <div style={{ display: 'flex', gap: 12 }}>
                {onRetry && (
                  <button
                    type="button"
                    onClick={onRetry}
                    style={{
                      padding: '11px 22px', borderRadius: 999,
                      background: SURFACE_HOVER,
                      border: `1px solid ${COLOR_BORDER_LIGHT}`,
                      color: COLOR_TEXT, fontSize: 13, fontWeight: 500, cursor: 'pointer',
                      fontFamily: FONT_SANS,
                      transition: 'background 0.2s, border-color 0.2s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(245,240,235,0.1)';
                      e.currentTarget.style.borderColor = COLOR_BORDER_LIGHT;
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = SURFACE_HOVER;
                      e.currentTarget.style.borderColor = COLOR_BORDER_LIGHT;
                    }}
                  >
                    {getString('demo.tryAgain')}
                  </button>
                )}
                {bookable && (
                  <a
                    href={bookingHref!}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => handleBookingLinkClick(event, bookingHref!)}
                    style={{
                      padding: '11px 22px', borderRadius: 999,
                      background: `linear-gradient(135deg, ${COLOR_ACCENT} 0%, ${COLOR_ACCENT_STRONG} 100%)`,
                      border: 'none',
                      color: COLOR_BG, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      textDecoration: 'none', display: 'inline-flex', alignItems: 'center',
                      fontFamily: FONT_SANS,
                      boxShadow: `0 10px 28px -10px ${COLOR_ACCENT_GLOW}`,
                      transition: 'all 0.2s cubic-bezier(0.16,1,0.3,1)',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = `linear-gradient(135deg, ${COLOR_ACCENT_HOVER} 0%, ${COLOR_ACCENT} 100%)`;
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = `linear-gradient(135deg, ${COLOR_ACCENT} 0%, ${COLOR_ACCENT_STRONG} 100%)`;
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {bookLiveDemoLabel}
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Post-demo: the screencast frame becomes a full-bleed Cal.com /
            Calendly iframe so the prospect picks a slot without leaving the
            demo surface. Identity recap (headline, summary, features list,
            next-step) lives in the sidebar — see the showPostDemo branch in
            the sidebar render below. Hobbes-style split: action on the
            left, context on the right.

            The iframe-host allowlist and the href scheme guard that used to be
            two inline IIFEs here now live in ../sdk/calendar-embed, because the
            website agent's in-panel calendar needs exactly the same two
            answers — and a security check with two copies eventually has one
            stale copy. See that module for why the embed is allowlisted and the
            link only scheme-checked. */}
        {showPostDemo && (() => {
          const { embedSrc } = postDemoCalendar;
          const safeFallbackHref = postDemoCalendar.fallbackHref;
          return (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10,
            background: COLOR_BG,
            display: 'flex',
          }}>
            {postDemoBookingPending ? (
              // Keep the provider frame unmounted while the bounded attribution
              // lookup runs. The existing next-step copy is an honest neutral
              // state and avoids introducing a fourth loading-string surface.
              <div
                aria-busy="true"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 40,
                }}
              >
                <p style={{
                  fontFamily: FONT_DISPLAY,
                  color: COLOR_TEXT,
                  fontSize: 26,
                  margin: 0,
                  textAlign: 'center',
                }}>
                  {getString('demo.nextSteps')}
                </p>
              </div>
            ) : embedSrc ? (
              // Full-bleed iframe. allow="camera microphone clipboard-write"
              // covers Cal.com's optional integration prompts; we keep
              // colorScheme: dark so the Cal default skin matches the demo
              // chrome rather than flashing white.
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <iframe
                  ref={bookingFrameRef}
                  src={embedSrc}
                  title="Schedule a follow-up"
                  style={{
                    width: '100%',
                    flex: 1,
                    minHeight: 0,
                    border: 'none',
                    display: 'block',
                    colorScheme: 'dark',
                  }}
                  allow="camera; microphone; clipboard-write"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
                <a
                  href={safeFallbackHref!}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => handleBookingLinkClick(event, safeFallbackHref!)}
                  style={{ padding: '10px 16px', color: COLOR_TEXT, fontFamily: FONT_SANS, fontSize: 13, textAlign: 'right' }}
                >
                  {getString('wa.booking.openNewTab')}
                </a>
              </div>
            ) : (
              // No embed-friendly URL: centered fallback with the "Book a
              // call" link (when available) or a quiet "Demo complete" stub.
              // The recap content in the sidebar still gives the prospect
              // context about what happened.
              <div style={{
                flex: 1,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                gap: 24, padding: 40,
              }}>
                <div aria-hidden style={{
                  position: 'absolute', top: '20%', left: '50%',
                  transform: 'translateX(-50%)',
                  width: 700, height: 700,
                  background: 'radial-gradient(circle, rgba(255,92,53,0.10) 0%, transparent 65%)',
                  filter: 'blur(80px)',
                  pointerEvents: 'none',
                }} />
                <p style={{
                  position: 'relative',
                  fontFamily: FONT_DISPLAY,
                  color: COLOR_TEXT, fontSize: 26, fontWeight: 400,
                  letterSpacing: '-0.01em',
                  margin: 0, textAlign: 'center',
                }}>
                  {getString('demo.nextSteps')}
                </p>
                {safeFallbackHref && (
                  <a
                    href={safeFallbackHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => handleBookingLinkClick(event, safeFallbackHref)}
                    style={{
                      position: 'relative',
                      padding: '12px 26px', borderRadius: 999,
                      background: `linear-gradient(135deg, ${COLOR_ACCENT} 0%, ${COLOR_ACCENT_STRONG} 100%)`,
                      border: 'none',
                      color: COLOR_BG, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                      textDecoration: 'none', display: 'inline-flex', alignItems: 'center',
                      fontFamily: FONT_SANS,
                      boxShadow: `0 10px 28px -10px ${COLOR_ACCENT_GLOW}`,
                      transition: 'all 0.2s cubic-bezier(0.16,1,0.3,1)',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = `linear-gradient(135deg, ${COLOR_ACCENT_HOVER} 0%, ${COLOR_ACCENT} 100%)`;
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = `linear-gradient(135deg, ${COLOR_ACCENT} 0%, ${COLOR_ACCENT_STRONG} 100%)`;
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {bookCallLabel}
                  </a>
                )}
              </div>
            )}
          </div>
          );
        })()}

        {/* Loading state — branded with customer logo + name.
            When CompanyLogo can render an actual image (logoUrl OR the
            self-hosted faviconUrl), we drop the redundant siteName text — the
            visual mark IS the identity. Only when we'd fall all the way back to
            a letter-mark do we keep the siteName text, since a single letter
            alone is too ambiguous. */}
        {!videoReady && !showError && !showPostDemo && (() => {
          const hasRealLogo = !!siteLogoUrl || !!siteFaviconUrl;
          return (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{ marginBottom: hasRealLogo ? 26 : siteName ? 18 : 22 }}>
              <CompanyLogo
                name={siteName || ''}
                logoUrl={siteLogoUrl || ''}
                faviconUrl={siteFaviconUrl || ''}
                size={64}
              />
            </div>
            {siteName && !hasRealLogo && (
              <p style={{
                fontFamily: FONT_DISPLAY,
                color: COLOR_TEXT, fontSize: 26, fontWeight: 400,
                margin: '0 0 24px', letterSpacing: '-0.01em',
              }}>
                {siteName}
              </p>
            )}
            <div style={{
              width: 28, height: 28,
              border: `2px solid ${COLOR_BORDER}`, borderTopColor: COLOR_ACCENT,
              borderRadius: '50%', animation: 'floe-demo-spin 0.8s linear infinite',
            }} />
            <p style={{
              fontFamily: FONT_SANS,
              color: COLOR_TEXT_DIM, marginTop: 16, fontSize: 13, fontWeight: 500,
              maxWidth: 320, textAlign: 'center', lineHeight: 1.5,
            }}>
              {!connected
                ? 'Connecting.'
                : slowConnect
                  ? `Still loading ${siteName || 'your demo'}.`
                  : siteName
                    ? `Loading ${siteName}.`
                    : 'Loading.'}
            </p>
          </div>
          );
        })()}

        <ScreencastVideo
          videoRef={videoRef}
          videoReady={videoReady}
          onPlaying={handleVideoPlaying}
        />

        {/* Subtitle overlay over the live video. Warm tinted background
            with cream text, matching the rest of the SDK chrome. */}
        {/* Subtitle overlay — only when the sidebar is CLOSED.
            With the sidebar open, the chat panel already streams the
            full transcript; a duplicate subtitle on the video would
            compete with that text and just be visual noise. With the
            sidebar closed, the chat is hidden — the subtitle is the
            user's only way to read what the bot is saying. */}
        {!sidebarOpen && botTranscript && botIsSpeaking && (
          <div style={{
            position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            maxWidth: '70%',
            background: 'rgba(12,10,9,0.82)',
            backdropFilter: 'blur(12px)',
            borderRadius: 12, padding: '10px 20px',
            border: `1px solid ${COLOR_BORDER}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}>
            <p style={{
              fontFamily: FONT_SANS,
              color: COLOR_TEXT, fontSize: 14, lineHeight: 1.5, margin: 0, textAlign: 'center',
              // Clamp the floating caption so a long transcript can't grow
              // unbounded and cover half the screen. The full text still
              // lives in the sidebar/transcript; this is a glanceable summary.
              // Tighter on mobile where vertical space is scarcer.
              display: '-webkit-box',
              WebkitLineClamp: isMobile ? 2 : 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {botTranscript}
            </p>
          </div>
        )}
        </div>
      </div>

      {/* ════════════════════════════════════════
          RIGHT: Chat sidebar (collapsible)
          ════════════════════════════════════════ */}
      {/* Borderless sidebar — shares the main canvas background (COLOR_BG)
          so the whole panel reads as one continuous surface. Spacing and
          the framed video on the left do all the visual separation;
          there's no elevated card on the right. */}
      {sidebarOpen ? (
        <div style={{
          // Desktop: fixed-width column beside the video. Mobile: full-screen
          // overlay above it — a 360px sibling would crush the video/calendar
          // pane to a sliver on a phone (FLO-124). Driven live by isMobile.
          ...sidebarContainerLayout(isMobile, SIDEBAR_WIDTH),
          display: 'flex', flexDirection: 'column',
          background: COLOR_BG,
          fontFamily: FONT_SANS,
          color: COLOR_TEXT,
        }}>

          {/* ─── Avatar PiP (when the agent has a live avatar) ─── */}
          {/* Shared component (FLO-545) — owns the <video> attachment, the
              speakerMuted sync, and the padded-slot framing that matches the
              screencast frame on the left. Renders nothing without a stream. */}
          <AvatarPiP
            stream={avatarStream ?? null}
            idleLoopUrl={avatarIdleLoopUrl}
            posterUrl={avatarPosterUrl}
            speakerMuted={speakerMuted}
          />

          {/* ─── Customer brand header ─── */}
          {/* Single primary identity row. Floe used to have a status row
              right below this — moved to the "Powered by Floe" line at the
              bottom of the sidebar so the demoed product owns the top of
              the panel uncontested. */}
          <div style={{
            padding: '18px 14px 16px 18px',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <CompanyLogo
              name={siteName || ''}
              logoUrl={siteLogoUrl || ''}
              faviconUrl={siteFaviconUrl || ''}
              size={36}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 15, fontWeight: 600, color: COLOR_TEXT,
                letterSpacing: '-0.01em', lineHeight: 1.15,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {siteName || 'Live demo'}
              </div>
              <div style={{
                fontSize: 11, color: COLOR_TEXT_DIM, fontWeight: 500,
                marginTop: 3, letterSpacing: '0.02em',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span>{getString('demo.liveDemo')}</span>
                {siteDomain && (
                  <>
                    <span style={{ opacity: 0.4 }}>·</span>
                    <span style={{
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      maxWidth: 160,
                    }}>
                      {siteDomain.replace(/^www\./, '')}
                    </span>
                  </>
                )}
              </div>
            </div>

            {onLanguageChange && (
              <LanguageSelector
                languages={languageOptions}
                value={selectedLanguage}
                pending={pendingLanguageSwitch}
                error={languageSwitchError}
                disabled={languageSwitchDisabled}
                appearance="dark"
                onChange={onLanguageChange}
              />
            )}

            {/* Header right-edge button. During the demo it collapses the
                sidebar ("Hide chat"). In the post-demo wrap-up on DESKTOP it
                becomes the Close (X) — the recap sits permanently beside the
                calendar, so there's nothing to collapse to; firing the
                configurable close action (host → landing). On MOBILE post-demo
                the recap is a full-screen overlay over the calendar, so the
                button collapses back to the calendar instead; the labelled
                "Close demo" button at the foot of the recap is the exit. */}
            <button
              type="button"
              onClick={headerButtonClosesDemo(showPostDemo, isMobile) ? () => onCloseDemo() : () => setSidebarOpen(false)}
              title={getString(headerButtonClosesDemo(showPostDemo, isMobile) ? 'demo.close' : 'demo.hideChat')}
              aria-label={getString(headerButtonClosesDemo(showPostDemo, isMobile) ? 'demo.close' : 'demo.hideChat')}
              style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'transparent',
                border: 'none',
                color: COLOR_TEXT_DIM,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                transition: 'background 0.15s, color 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = SURFACE_HOVER;
                e.currentTarget.style.color = COLOR_TEXT;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = COLOR_TEXT_DIM;
              }}
            >
              {headerButtonClosesDemo(showPostDemo, isMobile) ? (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              )}
            </button>
          </div>

          {showPostDemo ? (
            // ─── Post-demo recap ───
            // Replaces the messages region with the success summary that
            // used to live in a big centered overlay on the LEFT panel.
            // LEFT is now the calendar iframe; this is the matching context.
            <div className="floe-demo-msgs" style={{
              flex: 1, overflowY: 'auto', padding: '4px 16px 16px',
              display: 'flex', flexDirection: 'column', gap: 18,
            }}>
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                gap: 14, paddingTop: 6,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: COLOR_ACCENT,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: `0 8px 22px -8px ${COLOR_ACCENT_GLOW}`,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COLOR_BG} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <p style={{
                  fontFamily: FONT_DISPLAY,
                  color: COLOR_TEXT, fontSize: 22, fontWeight: 400,
                  letterSpacing: '-0.01em', lineHeight: 1.15,
                  margin: 0,
                }}>
                  That&apos;s the demo.
                </p>
                <p style={{
                  fontFamily: FONT_SANS,
                  color: COLOR_TEXT_DIM, fontSize: 12, margin: 0,
                  lineHeight: 1.5,
                }}>
                  {isMobile
                    ? 'Close this to pick a time and keep the conversation going.'
                    : 'Pick a time on the left to keep the conversation going.'}
                </p>
              </div>

              {demoSummary && (
                <p style={{
                  fontFamily: FONT_SANS,
                  color: COLOR_TEXT_MUTED, fontSize: 13,
                  margin: 0, lineHeight: 1.6,
                  opacity: 0.9,
                }}>
                  {demoSummary}
                </p>
              )}

              {featuresShown && featuresShown.length > 0 && (
                <div>
                  <p style={{
                    fontFamily: FONT_SANS,
                    color: COLOR_TEXT_DIM, fontSize: 10,
                    textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600,
                    margin: '0 0 10px',
                  }}>
                    {getString('demo.recapTitle')}
                  </p>
                  <ul style={{
                    listStyle: 'none', padding: 0, margin: 0,
                    display: 'flex', flexDirection: 'column', gap: 6,
                  }}>
                    {featuresShown.map((feature, i) => (
                      <li key={i} style={{
                        fontFamily: FONT_SANS,
                        color: COLOR_TEXT, fontSize: 13, lineHeight: 1.5,
                        paddingLeft: 16, position: 'relative',
                        opacity: 0.85,
                      }}>
                        <span style={{
                          position: 'absolute', left: 0, top: 7,
                          width: 4, height: 4, borderRadius: '50%',
                          background: COLOR_ACCENT,
                        }} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {nextStep && (
                <div style={{
                  padding: '12px 14px', borderRadius: 12,
                  background: COLOR_ACCENT_MUTED,
                  border: `1px solid ${COLOR_ACCENT_15}`,
                }}>
                  <p style={{
                    fontFamily: FONT_SANS,
                    color: COLOR_ACCENT, fontSize: 9,
                    textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 600,
                    margin: '0 0 4px',
                  }}>
                    {getString('demo.nextLabel')}
                  </p>
                  <p style={{
                    fontFamily: FONT_SANS,
                    color: COLOR_TEXT, fontSize: 13, lineHeight: 1.5, margin: 0,
                  }}>
                    {nextStep}
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* ─── Messages ─── */
            <div className="floe-demo-msgs" style={{
              flex: 1, overflowY: 'auto', padding: '4px 16px 16px',
              display: 'flex', flexDirection: 'column', gap: 14,
            }}>
              {displayMessages.length === 0 && (
                <div style={{
                  flex: 1, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 10,
                  paddingBottom: 32,
                }}>
                  {!connected ? (
                    <div style={{
                      width: 22, height: 22,
                      border: `2px solid ${COLOR_BORDER}`, borderTopColor: sc,
                      borderRadius: '50%', animation: 'floe-demo-spin 0.8s linear infinite',
                    }} />
                  ) : (
                    <svg width="28" height="24" viewBox="0 0 159 138" fill="none" style={{ opacity: 0.4 }}>
                      <path fill={COLOR_TEXT_DIM} d="M64 31.5c-23.5 0-34 18-64 48.5C0 31.5 26.5 1 54 1s105-1 105-1c-2 11.5-18.5 31.5-29.5 31.5z"/>
                      <path fill={COLOR_TEXT_DIM} d="M64.5 89.5c-23.5 0-34 18-64 48.5 0-48.5 26.5-79 54-79s75-1 75-1c-2 11.5-18.5 31.5-29.5 31.5z"/>
                    </svg>
                  )}
                  <p style={{ color: COLOR_TEXT_DIM, fontSize: 12, margin: 0 }}>
                    {connected ? getString('demo.waiting') : getString('demo.connecting')}
                  </p>
                </div>
              )}
              {displayMessages.map((msg) => (
                <DemoMessageBubble key={msg.id} message={msg} />
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* ─── Footer ───
              Three vertical bands, all borderless:
                (1) "Powered by Floe" + agent state pill — replaces the
                    old top-of-sidebar Floe row. Doubles as the single
                    visible "connecting / speaking" indicator.
                (2) Input row — text + send only. Mic moved out.
                (3) Controls row — mic mute, speaker mute, end demo.
                    All icon-only, equal-sized, centered. */}
          <div style={{
            padding: '12px 14px 14px',
            display: 'flex', flexDirection: 'column', gap: 10,
            background: COLOR_BG,
          }}>
            {/* (1) Powered by Floe — agent identity at the bottom now.
                Anchor wraps the orb + label only (NOT the state pill);
                clicking attribution opens floe.so in a new tab. */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '0 4px 4px',
            }}>
              <a
                href="https://floe.so"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Powered by Floe — visit floe.so"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  textDecoration: 'none',
                  color: COLOR_TEXT_DIM,
                  transition: 'color 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = COLOR_TEXT)}
                onMouseLeave={(e) => (e.currentTarget.style.color = COLOR_TEXT_DIM)}
              >
                <svg width="11" height="9" viewBox="0 0 159 138" fill="none" style={{ flexShrink: 0 }}>
                  <path fill={sc} d="M64 31.5c-23.5 0-34 18-64 48.5C0 31.5 26.5 1 54 1s105-1 105-1c-2 11.5-18.5 31.5-29.5 31.5z"/>
                  <path fill={sc} d="M64.5 89.5c-23.5 0-34 18-64 48.5 0-48.5 26.5-79 54-79s75-1 75-1c-2 11.5-18.5 31.5-29.5 31.5z"/>
                </svg>
                <span style={{
                  fontSize: 10, fontWeight: 500,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  color: 'inherit',
                }}>
                  Powered by Floe
                </span>
              </a>
              <span style={{
                marginLeft: 'auto',
                fontSize: 10, color: sc, fontWeight: 600,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                display: 'inline-flex', alignItems: 'center', gap: 5,
                transition: 'color 0.3s',
              }}>
                <span style={{
                  width: 5, height: 5, borderRadius: '50%',
                  background: sc, boxShadow: `0 0 6px ${sc}`,
                }} />
                {sl}
              </span>
            </div>

            {/* Input + controls are call-time affordances — hidden once the
                demo has ended (post-demo calendar / wrap-up): the prospect is
                booking or leaving, so mic / speaker / End-demo no longer apply. */}
            {!showPostDemo && (<>
            {/* (1.5) Opener chips (FLO-307) — deterministic one-tap ways into
                the demo, server-pushed at kickoff so they're guaranteed on turn
                1. Coral + tappable; disabled until `connected` so a stray tap
                during the connecting window can't fire. Tapping sends the chip
                text as a normal user turn; the SDK clears the row on the next
                turn, so this whole block disappears after turn 1. */}
            {suggestedOptions && suggestedOptions.length > 0 && (
              <div
                role="group"
                aria-label="Suggested openers"
                style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}
              >
                {suggestedOptions.map((opt, i) => (
                  <button
                    key={`${i}-${opt}`}
                    type="button"
                    onClick={() => { if (connected) onSelectOption?.(opt); }}
                    disabled={!connected}
                    title={opt}
                    onMouseEnter={(e) => {
                      if (connected) e.currentTarget.style.background = COLOR_ACCENT_20;
                    }}
                    onMouseLeave={(e) => {
                      if (connected) e.currentTarget.style.background = COLOR_ACCENT_MUTED;
                    }}
                    style={{
                      maxWidth: '100%',
                      padding: '6px 12px', borderRadius: 999,
                      border: `1px solid ${connected ? COLOR_ACCENT_40 : COLOR_BORDER}`,
                      background: connected ? COLOR_ACCENT_MUTED : 'transparent',
                      color: connected ? COLOR_ACCENT : COLOR_TEXT_DIM,
                      fontSize: 12.5, fontWeight: 500, fontFamily: FONT_SANS,
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      cursor: connected ? 'pointer' : 'default',
                      transition: 'background 0.15s, border-color 0.15s',
                    }}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}
            <IdentityComposerHint ask={identityAsk} onReject={() => { consumeIdentityDraft(); onIdentityReject?.(); }} disabled={!connected} />
            {/* (2) Input row — text + send. Borderless surface. */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 8px 8px 12px',
              background: SURFACE_FAINT, borderRadius: 12,
            }}>
              <input
                ref={textInputRef}
                {...identityInputProps}
                type="text" value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleTextSubmit()}
                placeholder={connected ? activeIdentity ? getString(`identity.placeholder.${activeIdentity.field}`) : getString('demo.typeMessage') : getString('demo.connecting')}
                disabled={!connected}
                style={{
                  flex: 1, padding: '4px 0', border: 'none', background: 'transparent',
                  color: connected ? COLOR_TEXT : COLOR_TEXT_DIM,
                  fontSize: 16, outline: 'none', caretColor: COLOR_ACCENT, fontFamily: FONT_SANS,
                }}
              />
              <button
                type="button"
                onClick={handleTextSubmit}
                disabled={!textInput.trim() || !connected}
                aria-label={confirmMode ? getString('identity.confirm') : getString('wa.send')}
                style={{
                  width: 28, height: 28, borderRadius: 8, border: 'none',
                  background: textInput.trim() && connected ? COLOR_ACCENT : 'transparent',
                  color: textInput.trim() && connected ? COLOR_BG : COLOR_TEXT_DIM,
                  cursor: textInput.trim() && connected ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  boxShadow: textInput.trim() && connected ? `0 4px 12px -4px ${COLOR_ACCENT_GLOW}` : 'none',
                  transition: 'all 0.2s',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>

            {/* (3) Controls row — mic, speaker, end demo. Icon-only,
                equal hit areas, centered. End demo gets the coral tint to
                signal it's the destructive action without going full red. */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 8,
            }}>
              <IconToggle
                active={micEnabled}
                onClick={onToggleMute}
                title={micEnabled ? 'Mute mic' : 'Unmute mic'}
                ariaLabel={micEnabled ? 'Mute microphone' : 'Unmute microphone'}
              >
                {micEnabled ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                    <line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="1" y1="1" x2="23" y2="23" />
                    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .76-.12 1.49-.34 2.18" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                    <line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                )}
              </IconToggle>

              <IconToggle
                active={!speakerMuted}
                onClick={() => onToggleSpeaker?.()}
                title={speakerMuted ? 'Unmute speaker' : 'Mute speaker'}
                ariaLabel={speakerMuted ? 'Unmute speaker' : 'Mute speaker'}
              >
                {!speakerMuted ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  </svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                )}
              </IconToggle>

              <IconToggle
                active={true}
                tone="danger"
                onClick={() => setShowEndConfirm(true)}
                title="End demo"
                ariaLabel="End demo"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              </IconToggle>
            </div>
            </>)}
            {/* Post-demo: a clear, labelled way out at the bottom of the
                sidebar — same configurable close action as the header X and
                End Call (host → landing page). */}
            {showPostDemo && (
              <button
                type="button"
                onClick={() => onCloseDemo()}
                style={{
                  width: '100%',
                  padding: '11px 16px', borderRadius: 12,
                  background: SURFACE_HOVER,
                  border: `1px solid ${COLOR_BORDER_LIGHT}`,
                  color: COLOR_TEXT, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  fontFamily: FONT_SANS,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'background 0.18s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(245,240,235,0.1)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = SURFACE_HOVER)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                {getString('demo.close')}
              </button>
            )}
          </div>
        </div>
      ) : (
        /* ─── Collapsed: top-center brand + top-right open icon +
                          bottom control bar + bottom-right Floe tag ─── */
        <>
          {onLanguageChange && (
            <div style={{ position: 'absolute', top: 16, left: 18, zIndex: 101 }}>
              <LanguageSelector
                languages={languageOptions}
                value={selectedLanguage}
                pending={pendingLanguageSwitch}
                error={languageSwitchError}
                disabled={languageSwitchDisabled}
                appearance="dark"
                onChange={onLanguageChange}
              />
            </div>
          )}
          {/* Top-center brand cluster — drops in from above when the
              sidebar closes. Same logo + name as the sidebar header,
              just slightly smaller and on one line. Sits in the 64px
              top padding zone alongside the open-chat icon. */}
          <div
            style={{
              position: 'absolute', top: 18, left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex', alignItems: 'center', gap: 10,
              maxWidth: 'calc(100% - 200px)', minWidth: 0,
              zIndex: 100,
              animation: 'floe-demo-brand-drop 0.4s cubic-bezier(0.16,1,0.3,1)',
              pointerEvents: 'none',
            }}
            aria-hidden
          >
            <CompanyLogo
              name={siteName || ''}
              logoUrl={siteLogoUrl || ''}
              faviconUrl={siteFaviconUrl || ''}
              size={28}
            />
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <div style={{
                fontSize: 14, fontWeight: 600, color: COLOR_TEXT,
                letterSpacing: '-0.01em', lineHeight: 1.15,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {siteName || 'Live demo'}
              </div>
              {siteDomain && (
                <div style={{
                  fontSize: 10, color: COLOR_TEXT_DIM, fontWeight: 500,
                  marginTop: 1, letterSpacing: '0.04em',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {getString('demo.liveDemo')} · {siteDomain.replace(/^www\./, '')}
                </div>
              )}
            </div>
          </div>

          {/* Top-right "open chat" icon. Lives in the canvas's top
              padding zone (64px when sidebar is closed) so it never
              overlaps the framed video. Carries a state-coloured dot so
              the prospect can see the agent is still alive even with the
              sidebar hidden. */}
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            title="Open chat"
            aria-label="Open chat"
            style={{
              position: 'absolute', top: 18, right: 22,
              width: 40, height: 40, borderRadius: '50%',
              background: COLOR_BG_ELEVATED,
              border: `1px solid ${COLOR_BORDER}`,
              boxShadow: `0 10px 28px -10px rgba(0,0,0,0.55), 0 0 14px ${sc}26`,
              color: sc,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 100,
              transition: 'transform 0.2s, box-shadow 0.2s, color 0.3s, border-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.06)';
              e.currentTarget.style.boxShadow = `0 14px 36px -8px rgba(0,0,0,0.6), 0 0 22px ${sc}44`;
              e.currentTarget.style.borderColor = `${sc}44`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = `0 10px 28px -10px rgba(0,0,0,0.55), 0 0 14px ${sc}26`;
              e.currentTarget.style.borderColor = COLOR_BORDER;
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            <span aria-hidden style={{
              position: 'absolute', top: 4, right: 4,
              width: 8, height: 8, borderRadius: '50%',
              background: sc, boxShadow: `0 0 6px ${sc}`,
              border: `2px solid ${COLOR_BG_ELEVATED}`,
              transition: 'background 0.3s',
            }} />
          </button>

          {/* Bottom-center control bar — text input + send + mic +
              speaker + end demo. Lives in the canvas's bottom padding
              zone (112px when sidebar is closed) so it never overlaps
              the framed video. Width capped at ~520px so it doesn't
              sprawl on wide monitors. Hidden once the demo has ended — the
              prospect is on the calendar / wrap-up, not on a live call. */}
          {!showPostDemo && (
          <div
            style={{
              position: 'absolute', bottom: 24, left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 100,
              animation: 'floe-demo-controls-rise 0.35s cubic-bezier(0.16,1,0.3,1)',
              width: 'min(520px, calc(100% - 48px))',
            }}
          >
            <div style={{ background: COLOR_BG_ELEVATED, borderRadius: 12 }}><IdentityComposerHint ask={identityAsk} onReject={() => { consumeIdentityDraft(); onIdentityReject?.(); }} disabled={!connected} /></div>
            <div style={{
              display: 'flex',
              // On mobile we have vertical room — stack the input over a
              // grouped row of call icons rather than overflowing one row.
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: 8,
              padding: 8,
              background: COLOR_BG_ELEVATED,
              borderRadius: 22,
              border: `1px solid ${COLOR_BORDER}`,
              boxShadow: '0 14px 40px -10px rgba(0,0,0,0.65), 0 2px 8px rgba(0,0,0,0.3)',
            }}>
              {/* Text input — fills the left side. Same placeholder /
                  disabled behavior as the in-sidebar one. */}
              <div style={{
                flex: 1, minWidth: 0,
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '0 4px 0 14px',
              }}>
                <input
                  {...identityInputProps}
                  ref={controlInputRef}
                  type="text"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleTextSubmit()}
                  placeholder={connected ? activeIdentity ? getString(`identity.placeholder.${activeIdentity.field}`) : getString('demo.typeMessage') : getString('demo.connecting')}
                  disabled={!connected}
                  style={{
                    flex: 1, minWidth: 0,
                    padding: '10px 0', border: 'none', background: 'transparent',
                    color: connected ? COLOR_TEXT : COLOR_TEXT_DIM,
                    fontSize: 16, outline: 'none',
                    caretColor: COLOR_ACCENT, fontFamily: FONT_SANS,
                  }}
                />
                <button
                  type="button"
                  onClick={handleTextSubmit}
                  disabled={!textInput.trim() || !connected}
                  aria-label={confirmMode ? getString('identity.confirm') : getString('wa.send')}
                  style={{
                    width: 32, height: 32, borderRadius: 10, border: 'none',
                    background: textInput.trim() && connected ? COLOR_ACCENT : 'transparent',
                    color: textInput.trim() && connected ? COLOR_BG : COLOR_TEXT_DIM,
                    cursor: textInput.trim() && connected ? 'pointer' : 'default',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: textInput.trim() && connected ? `0 4px 12px -4px ${COLOR_ACCENT_GLOW}` : 'none',
                    transition: 'all 0.2s',
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </button>
              </div>

              {/* Visual separator between chat surface + call controls.
                  Hidden when the bar stacks on mobile — a vertical divider
                  reads as noise between two horizontal rows. */}
              <span aria-hidden style={{
                width: 1, height: 24,
                background: COLOR_BORDER,
                margin: '0 2px',
                display: isMobile ? 'none' : 'block',
              }} />

              {/* Call-control icons — grouped so they drop to a second
                  row as one unit on mobile, where the bar stacks. */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                justifyContent: isMobile ? 'center' : 'flex-start',
              }}>
              <IconToggle
                active={micEnabled}
                onClick={onToggleMute}
                title={micEnabled ? 'Mute mic' : 'Unmute mic'}
                ariaLabel={micEnabled ? 'Mute microphone' : 'Unmute microphone'}
              >
                {micEnabled ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                    <line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="1" y1="1" x2="23" y2="23" />
                    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .76-.12 1.49-.34 2.18" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                    <line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                )}
              </IconToggle>

              <IconToggle
                active={!speakerMuted}
                onClick={() => onToggleSpeaker?.()}
                title={speakerMuted ? 'Unmute speaker' : 'Mute speaker'}
                ariaLabel={speakerMuted ? 'Unmute speaker' : 'Mute speaker'}
              >
                {!speakerMuted ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  </svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                )}
              </IconToggle>

              <IconToggle
                active
                tone="danger"
                onClick={() => setShowEndConfirm(true)}
                title="End demo"
                ariaLabel="End demo"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              </IconToggle>
              </div>
            </div>
          </div>
          )}

          {/* "Powered by Floe" bottom-right tag. Sits in the same
              112px bottom padding zone as the control bar — vertically
              centered against the bar so they read as one cluster. Low-
              key typography: small caps, dim text, just the orb for the
              brand mark. No background or border — floats free.
              Anchored to floe.so so prospects can click through; pointer-
              events flipped on (was off) to make the link reachable. */}
          <a
            href="https://floe.so"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Powered by Floe — visit floe.so"
            style={{
              position: 'absolute', bottom: 38, right: 24,
              display: 'flex', alignItems: 'center', gap: 6,
              opacity: 0.75,
              zIndex: 90,
              textDecoration: 'none',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.75')}
          >
            <svg width="10" height="9" viewBox="0 0 159 138" fill="none" style={{ flexShrink: 0 }}>
              <path fill={COLOR_TEXT_DIM} d="M64 31.5c-23.5 0-34 18-64 48.5C0 31.5 26.5 1 54 1s105-1 105-1c-2 11.5-18.5 31.5-29.5 31.5z"/>
              <path fill={COLOR_TEXT_DIM} d="M64.5 89.5c-23.5 0-34 18-64 48.5 0-48.5 26.5-79 54-79s75-1 75-1c-2 11.5-18.5 31.5-29.5 31.5z"/>
            </svg>
            <span style={{
              fontFamily: FONT_SANS,
              fontSize: 9, fontWeight: 500,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              color: COLOR_TEXT_DIM,
            }}>
              Powered by Floe
            </span>
          </a>
        </>
      )}

      {/* End-call confirmation modal — gates the destructive End demo
          action behind a Book Demo CTA. END_CONFIRM_MODAL_Z (30) puts it above
          the intro form (20), the post-demo overlay (10), and the mobile chat
          sidebar overlay (MOBILE_SIDEBAR_OVERLAY_Z, 25) — the End demo button
          that opens this lives in that sidebar's control row, so the modal must
          win. Clicking outside or pressing Escape dismisses (keep exploring).

          Book Demo → ends the demo into wrap-up (setUserEndedDemo + onEndDemo
          → endDemoForWrapUp), which sets demoEnded → showPostDemo and renders
          the Cal/Calendly calendar in place. End Call → onCloseDemo, the
          configurable close action (full teardown + 'closed' → host routes to
          the landing page) — the same action the overlay's close (X) fires.
          The prospect can't tab-trap past this modal once open because the
          underlying controls are behind a backdrop. */}
      {showEndConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Wrap up the demo"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowEndConfirm(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setShowEndConfirm(false);
          }}
          tabIndex={-1}
          style={{
            position: 'absolute', inset: 0, zIndex: END_CONFIRM_MODAL_Z,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(12, 10, 9, 0.78)',
            backdropFilter: 'blur(8px)',
            padding: '24px',
          }}
        >
          <div ref={endConfirmRef} style={{
            position: 'relative',
            maxWidth: 420, width: '100%',
            background: COLOR_BG,
            border: `1px solid ${COLOR_BORDER_LIGHT}`,
            borderRadius: 18,
            padding: '28px 28px 24px',
            boxShadow: '0 30px 80px -20px rgba(0,0,0,0.7)',
            display: 'flex', flexDirection: 'column', gap: 18,
          }}>
            <div>
              <p style={{
                fontFamily: FONT_DISPLAY,
                color: COLOR_TEXT, fontSize: 22, fontWeight: 400,
                letterSpacing: '-0.01em', lineHeight: 1.15,
                margin: '0 0 10px',
              }}>
                {getString('demo.wrapTitle')}
              </p>
              <p style={{
                fontFamily: FONT_SANS,
                color: COLOR_TEXT_DIM, fontSize: 13, lineHeight: 1.55,
                margin: 0,
              }}>
                {getString(bookable ? 'demo.wrapBody' : 'demo.wrapNoBookBody')}
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Book Demo — same effect as the natural demo-end:
                  setUserEndedDemo(true) tells the disconnect-suppression
                  logic this was intentional, onEndDemo() ends the live
                  session into wrap-up (endDemoForWrapUp, overlay kept
                  mounted). Then demoEnded → showPostDemo → the Cal/Calendly
                  embed renders in place.

                  ONLY rendered when a USABLE calendar URL is configured
                  (Site.calBookingUrl → getResolvedCalendarUrl → calendarLink →
                  resolveCalendarEmbed). Without one, "Book Demo" would just end
                  the demo onto an empty "next steps" stub — a silent dead-end —
                  so we hide it and the prospect only sees "End Call". This gate
                  reads the same resolution the post-demo screen renders from,
                  so the modal can no longer promise a booking that screen has
                  nothing to show for. Set the Cal/Calendly URL in Site Settings
                  to bring the booking CTA back. */}
              {bookable && (
              <button
                type="button"
                onClick={() => {
                  // CDP Phase 1: "Book Demo" here IS the booking CTA — it ends
                  // the demo into the Cal/Calendly embed (showPostDemo).
                  // `bookingHref` (non-null inside this `bookable` gate) is the
                  // sanitized URL the embed actually opens.
                  void onBookingCtaClick?.(bookingHref!);
                  setShowEndConfirm(false);
                  setUserEndedDemo(true);
                  onEndDemo();
                }}
                style={{
                  padding: '12px 24px', borderRadius: 999,
                  background: `linear-gradient(135deg, ${COLOR_ACCENT} 0%, ${COLOR_ACCENT_STRONG} 100%)`,
                  border: 'none',
                  color: COLOR_BG, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  fontFamily: FONT_SANS,
                  boxShadow: `0 10px 28px -10px ${COLOR_ACCENT_GLOW}`,
                  transition: 'transform 0.18s, box-shadow 0.18s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = `0 14px 32px -10px ${COLOR_ACCENT_GLOW}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 10px 28px -10px ${COLOR_ACCENT_GLOW}`;
                }}
              >
                {bookDemoLabel}
              </button>
              )}
              {/* End Call → the configurable close action (full teardown +
                  'closed' → host routes to the landing page). Same action the
                  post-demo overlay's close (X) button fires. */}
              <button
                type="button"
                onClick={() => {
                  setShowEndConfirm(false);
                  setUserEndedDemo(true);
                  onCloseDemo();
                }}
                style={{
                  padding: '12px 24px', borderRadius: 999,
                  background: SURFACE_HOVER,
                  border: `1px solid ${COLOR_BORDER_LIGHT}`,
                  color: COLOR_TEXT, fontSize: 13, fontWeight: 500, cursor: 'pointer',
                  fontFamily: FONT_SANS,
                  transition: 'background 0.18s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(245,240,235,0.1)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = SURFACE_HOVER)}
              >
                {getString('demo.endCall')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animations + scrollbar + markdown body. All cream-tinted so the
          message body styles match the rest of the warm palette. */}
      <style>{`
        @keyframes floe-demo-spin { to { transform: rotate(360deg); } }
        @keyframes messageSlide {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
        @keyframes floe-demo-controls-rise {
          from { opacity: 0; transform: translate(-50%, 16px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
        @keyframes floe-demo-brand-drop {
          from { opacity: 0; transform: translate(-50%, -16px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
        .floe-demo-markdown p { margin: 4px 0; line-height: 1.55; }
        .floe-demo-markdown p:first-child { margin-top: 0; }
        .floe-demo-markdown p:last-child { margin-bottom: 0; }
        .floe-demo-markdown strong { font-weight: 600; }
        .floe-demo-markdown ul, .floe-demo-markdown ol { margin: 6px 0; padding-left: 20px; }
        .floe-demo-markdown li { margin-bottom: 3px; line-height: 1.55; }
        .floe-demo-markdown code {
          background: rgba(245,240,235,0.1);
          padding: 1px 5px; border-radius: 4px;
          font-family: var(--font-jetbrains), 'JetBrains Mono', ui-monospace, monospace;
          font-size: 0.88em;
        }
        .floe-demo-markdown a { color: ${COLOR_ACCENT}; text-decoration: underline; text-underline-offset: 2px; }
        .floe-demo-markdown blockquote {
          border-left: 2px solid ${COLOR_ACCENT_40};
          margin: 6px 0; padding-left: 10px;
          color: ${COLOR_TEXT_MUTED};
        }
        .floe-demo-msgs::-webkit-scrollbar { width: 5px; }
        .floe-demo-msgs::-webkit-scrollbar-track { background: transparent; }
        .floe-demo-msgs::-webkit-scrollbar-thumb { background: rgba(245,240,235,0.08); border-radius: 3px; }
        .floe-demo-msgs::-webkit-scrollbar-thumb:hover { background: rgba(245,240,235,0.15); }
      `}</style>
    </div>
  );
};

// ──────────────────────────────────────
// Message bubble
// ──────────────────────────────────────

// Memoized: while a transcript streams, only the live (partial) bubble's
// `message` prop changes identity — completed bubbles keep a stable reference,
// so React.memo skips re-rendering the whole list on every token (FLO-110).
const DemoMessageBubble: React.FC<{ message: DemoMessage }> = React.memo(({ message }) => {
  const isUser = message.role === 'user';

  const renderedHtml = useMemo(() => {
    if (isUser || message.isPartial) return null;
    return renderMarkdown(message.content);
  }, [message.content, isUser, message.isPartial]);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: isUser ? 'flex-end' : 'flex-start',
      animation: 'messageSlide 0.3s ease-out',
      fontFamily: FONT_SANS,
    }}>
      <div style={{
        fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em',
        color: COLOR_TEXT_DIM, marginBottom: 5,
        paddingLeft: isUser ? 0 : 10, paddingRight: isUser ? 10 : 0,
      }}>
        {isUser ? getString('demo.you') : getString('demo.bot')}
      </div>

      {/* User messages: coral fill, warm-dark text. Floe messages: faint
          cream-tinted surface with cream text. Both feel like part of the
          warm coffee palette rather than the prior blue/white pair. */}
      <div style={{
        maxWidth: '90%', padding: '10px 14px',
        borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
        background: isUser
          ? `linear-gradient(135deg, ${COLOR_ACCENT} 0%, ${COLOR_ACCENT_STRONG} 100%)`
          : SURFACE_HOVER,
        color: isUser ? COLOR_BG : COLOR_TEXT,
        fontSize: 13, lineHeight: 1.55,
        boxShadow: isUser
          ? `0 6px 16px -4px ${COLOR_ACCENT_GLOW}`
          : '0 2px 8px rgba(0,0,0,0.2)',
        opacity: message.isPartial ? 0.75 : 1,
        border: isUser ? 'none' : `1px solid ${COLOR_BORDER}`,
        fontWeight: isUser ? 500 : 400,
      }}>
        {renderedHtml
          ? <div className="floe-demo-markdown" dangerouslySetInnerHTML={{ __html: renderedHtml }} />
          : message.content}
        {message.isPartial && (
          <span style={{
            display: 'inline-block', width: 3, height: 14,
            background: 'currentColor', marginLeft: 4,
            animation: 'blink 1s step-end infinite', verticalAlign: 'text-bottom',
            borderRadius: 1,
          }} />
        )}
      </div>

      {!message.isPartial && (
        <div style={{
          fontSize: 10, color: COLOR_TEXT_DIM, marginTop: 4, opacity: 0.6,
          paddingLeft: isUser ? 0 : 10, paddingRight: isUser ? 10 : 0,
        }}>
          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      )}
    </div>
  );
});
DemoMessageBubble.displayName = 'DemoMessageBubble';

// ──────────────────────────────────────
// Intro form — progressive disclosure pass.
//
// Stage 1 ("collect-email"): one field — work email. Big, prominent,
//   autofocused. Single "Continue" CTA. Nothing else competes for
//   attention. Matches the Linear / Attio / Vercel pattern of "ask the
//   minimum, only the minimum, until they commit."
//
// Stage 2 ("collect-rest"): email field stays (now with a small
//   confirmation), name field slides in beneath (required), then a
//   visually-distinct "Optional — helps tailor the demo" group reveals
//   Company + Role. The optional-ness is unambiguous via a divider, a
//   label on the group, and muted typography for the optional fields'
//   labels.
//
// Stage 3 ("submitting"): success card. Same shape as before.
//
// All transitions are CSS keyframes scoped to this component so they
// don't collide with the host page.
// ──────────────────────────────────────

// Aliases for the form-local code below. The canonical names are
// COLOR_ACCENT / COLOR_ACCENT_HOVER / COLOR_ACCENT_GLOW (defined with
// the rest of the design tokens). Keeping ACCENT etc. as short names
// avoids a sprawling rename throughout the form code.
const ACCENT = COLOR_ACCENT;
const ACCENT_BRIGHT = COLOR_ACCENT_HOVER;
const ACCENT_GLOW = COLOR_ACCENT_GLOW;


/**
 * Field renderer — underline-only, no card. Looks like typography that
 * happens to accept input, not a form widget. Focus state: the underline
 * animates from muted to coral and grows from 1px to 1.5px; the text
 * itself stays the focal point.
 *
 * Stage 1 ('lg'): 22px input text, generous bottom padding.
 * Stage 2 ('md'): 16px input text, tighter.
 */
const FormField: React.FC<{
  id: string;
  type?: string;
  label: string;
  placeholder?: string;
  value: string;
  optional?: boolean;
  autoFocus?: boolean;
  size?: 'lg' | 'md';
  onChange: (v: string) => void;
}> = ({ id, type = 'text', label, placeholder, value, optional, autoFocus, size = 'md', onChange }) => {
  const [focused, setFocused] = useState(false);
  const filled = !!value.trim();
  const big = size === 'lg';
  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: 'flex', alignItems: 'baseline', gap: 8,
          fontFamily: FONT_SANS,
          fontSize: 12, fontWeight: 500,
          color: focused ? ACCENT : filled ? COLOR_TEXT_MUTED : COLOR_TEXT_DIM,
          marginBottom: big ? 10 : 8,
          transition: 'color 0.2s',
        }}
      >
        <span>{label}</span>
        {optional && (
          <span style={{
            fontSize: 11, fontWeight: 400,
            color: COLOR_TEXT_DIM,
            opacity: 0.7,
            letterSpacing: '0.01em',
          }}>
            · optional
          </span>
        )}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          autoFocus={autoFocus}
          style={{
            width: '100%',
            padding: big ? '4px 0 14px' : '2px 0 10px',
            fontSize: big ? 24 : 17,
            lineHeight: 1.3,
            background: 'transparent',
            border: 'none',
            color: COLOR_TEXT, outline: 'none',
            fontFamily: FONT_SANS,
            fontWeight: big ? 500 : 400,
            letterSpacing: big ? '-0.015em' : '-0.005em',
          }}
        />
        {/* Static base underline — always visible at low opacity. */}
        <div
          aria-hidden
          style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            height: 1,
            background: 'rgba(245,240,235,0.1)',
          }}
        />
        {/* Animated focus underline — scales in from left to right. */}
        <div
          aria-hidden
          style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            height: 1.5,
            background: ACCENT,
            transform: focused ? 'scaleX(1)' : 'scaleX(0)',
            transformOrigin: 'left',
            transition: 'transform 0.35s cubic-bezier(0.16,1,0.3,1)',
            boxShadow: focused ? `0 0 12px ${ACCENT_GLOW}` : 'none',
          }}
        />
      </div>
    </div>
  );
};

/**
 * FLO-285: the docked entry pill.
 *
 * A minimized, bottom-centered fixed pill that captures a work email before the
 * demo goes full-bleed — the low-friction entry point for a marketing-site
 * embed. It renders standalone (DemoView early-returns it), so the host page
 * stays visible + interactive around it; only the pill captures pointer events.
 *
 * Submitting a valid email calls `onSubmitEmail`, which (in DemoView) both fires
 * the cost-gate that boots the pipecat session AND flips the overlay to
 * full-bleed. Matches the light pill in the FLO-285 design (product mark · email
 * · mic · send).
 */
const DemoDockedPill: React.FC<{
  siteName?: string;
  siteLogoUrl?: string;
  /** Brand accent CSS vars (FLO-64) — lets the product letter-mark stay on-brand.
   *  Undefined when no valid accent is set (Floe coral default). */
  accentVarStyle?: React.CSSProperties;
  /**
   * Does the pill ask for an email, or is it just a Start button?
   *
   * False when the site defers identity capture into the demo. The pill's
   * primary job — enter the demo without taking over the host page — is
   * unchanged; only the input goes away. The tap is still the cost gate.
   */
  collectEmail?: boolean;
  /** No-email cost gate, used when `collectEmail` is false. */
  onStart?: () => void;
  onSubmitEmail: (email: string) => void;
  checkEmail?: EmailCheck;
  languageOptions?: LanguageOption[];
  selectedLanguage?: SupportedLanguage;
  pendingLanguageSwitch?: PendingLanguageSwitch | null;
  languageSwitchError?: string | null;
  languageSwitchDisabled?: boolean;
  onLanguageChange?: (language: SupportedLanguage) => void;
}> = ({
  siteName,
  siteLogoUrl,
  accentVarStyle,
  collectEmail = true,
  onStart,
  onSubmitEmail,
  checkEmail,
  languageOptions = [],
  selectedLanguage = 'en',
  pendingLanguageSwitch,
  languageSwitchError,
  languageSwitchDisabled = false,
  onLanguageChange,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // Where "focus the pill" lands when there is no email input to focus.
  // Host-page CTAs dispatch `floe:expand-demo-pill` to surface this bot instead
  // of spawning a second overlay, and the logo doubles as an expand button —
  // both focused inputRef unconditionally, which under a deferred policy is a
  // ref to an element that is no longer rendered, i.e. a silent no-op.
  const startRef = useRef<HTMLButtonElement>(null);
  const [email, setEmail] = useState('');
  // Voice preference toggle. Visual for now — pre-session there's no mic to
  // drive; wiring this to the session's initial mic state is a follow-up.
  const [voicePreferred, setVoicePreferred] = useState(true);
  // Persistent-widget presence: collapse to a logo-only chip once the visitor
  // scrolls past a small threshold, so a docked embed on a long marketing page
  // stops hogging the bottom of every screen. Hover or focus re-expands it, as
  // does the `floe:expand-demo-pill` window event (dispatched by host-page CTAs
  // — e.g. floe-site's nav / "Start demo" buttons — so they surface this live
  // bot instead of spawning a second overlay).
  const [scrolled, setScrolled] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const expanded = !scrolled || hovered || focused;

  // With no email to collect there is nothing to validate — the button is
  // always live, exactly like the deferred Start card in the intro form.
  const emailRefused = useEmailRefused(email, collectEmail ? checkEmail : undefined);
  const valid = collectEmail ? EMAIL_RE.test(email.trim()) && !emailRefused : true;
  const submit = () => {
    if (!valid) return;
    if (collectEmail) onSubmitEmail(email.trim());
    else onStart?.();
  };

  const focusPill = useCallback(
    () =>
      requestAnimationFrame(() =>
        (collectEmail ? inputRef.current : startRef.current)?.focus(),
      ),
    [collectEmail],
  );

  // Collapse/expand on scroll (rAF-throttled — the pattern floe-site's floating
  // demo pill used before FLO-522 retired it).
  useEffect(() => {
    let raf = 0;
    const COLLAPSE_AT = 120;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() =>
        setScrolled((typeof window !== 'undefined' ? window.scrollY : 0) > COLLAPSE_AT),
      );
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Host-page CTAs expand + focus the pill via this event instead of launching
  // a duplicate demo. setScrolled(false) forces the expanded layout, then we
  // focus its control once it has laid out (the email input, or the Start
  // button when there is no input to focus).
  useEffect(() => {
    const onExpand = (e: Event) => {
      // Claim it, so the host page can tell a CTA that landed from one that fell
      // into a mode with no listener — see sdk/host-cta.ts for why that
      // distinction had to be made observable.
      e.preventDefault();
      setScrolled(false);
      focusPill();
    };
    window.addEventListener(HOST_DEMO_CTA_EVENT, onExpand);
    window.addEventListener(HOST_EXIT_INTENT_EVENT, onExpand);
    return () => {
      window.removeEventListener(HOST_DEMO_CTA_EVENT, onExpand);
      window.removeEventListener(HOST_EXIT_INTENT_EVENT, onExpand);
    };
  }, [focusPill]);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 24,
        transform: 'translateX(-50%)',
        zIndex: 2147483646,
        pointerEvents: 'auto',
        fontFamily: FONT_SANS,
        animation: 'floe-pill-rise 0.45s cubic-bezier(0.16,1,0.3,1)',
        ...accentVarStyle,
      }}
    >
      <style>{`
        @keyframes floe-pill-rise {
          from { opacity: 0; transform: translate(-50%, 16px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
        .floe-docked-pill-input::placeholder { color: ${COLOR_TEXT_DIM}; opacity: 1; }
      `}</style>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: expanded ? '10px 12px 10px 12px' : 8,
          background: '#FFFFFF',
          borderRadius: 999,
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow:
            '0 12px 40px -8px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.10)',
          transition: 'padding 0.35s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* Logo doubles as the collapsed chip — clicking it (when collapsed)
            expands + focuses the pill's control (email input, or Start). */}
        <button
          type="button"
          onClick={() => {
            setScrolled(false);
            focusPill();
          }}
          aria-label="Open the demo agent"
          style={{
            flexShrink: 0,
            border: 'none',
            background: 'transparent',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            lineHeight: 0,
          }}
        >
          <CompanyLogo
            name={siteName || 'Floe'}
            logoUrl={siteLogoUrl || ''}
            faviconUrl=""
            size={34}
          />
        </button>

        {/* Collapsible content — width + opacity animate between the full pill
            and the logo-only chip. Kept mounted (not unmounted) so the input
            stays focusable for the expand event. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            overflow: 'hidden',
            width: expanded ? 'min(500px, calc(100vw - 96px))' : 0,
            marginLeft: expanded ? 10 : 0,
            opacity: expanded ? 1 : 0,
            pointerEvents: expanded ? 'auto' : 'none',
            transition:
              'width 0.35s cubic-bezier(0.16,1,0.3,1), opacity 0.25s ease, margin 0.35s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          {onLanguageChange && (
            <LanguageSelector
              languages={languageOptions}
              value={selectedLanguage}
              pending={pendingLanguageSwitch}
              error={languageSwitchError}
              disabled={languageSwitchDisabled}
              onChange={onLanguageChange}
            />
          )}
          {collectEmail ? (
            <input
              ref={inputRef}
              className="floe-docked-pill-input"
              type="email"
              inputMode="email"
              autoComplete="email"
              aria-label={getString('demo.introEmail')}
              aria-invalid={emailRefused || undefined}
              title={emailRefused ? getString('demo.introEmailRefused') : undefined}
              placeholder={getString('demo.pillEmail')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              tabIndex={expanded ? 0 : -1}
              style={{
                flex: 1,
                minWidth: 0,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 16, // 16px avoids iOS Safari's focus-zoom
                fontFamily: FONT_SANS,
                color: '#1A1816',
                padding: '4px 2px',
              }}
            />
          ) : (
            // Deferred capture: the input's slot becomes the invitation. Kept as
            // a sibling of the same flex row (not a different layout) so the
            // pill's collapse/expand geometry is untouched.
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 15,
                fontFamily: FONT_SANS,
                color: '#1A1816',
                padding: '4px 2px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              See {siteName || 'it'} live — talk to the agent
            </span>
          )}
          {/* Voice preference (visual for now — see note above). */}
          <button
            type="button"
            onClick={() => setVoicePreferred((v) => !v)}
            aria-label={voicePreferred ? 'Voice on' : 'Voice off'}
            title={voicePreferred ? 'Voice on' : 'Voice off'}
            tabIndex={expanded ? 0 : -1}
            style={{
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: 'none',
              cursor: 'pointer',
              background: 'rgba(0,0,0,0.05)',
              color: voicePreferred ? '#1A1816' : COLOR_TEXT_DIM,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {voicePreferred ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .76-.12 1.49-.34 2.18" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            )}
          </button>
          {/* Submit — dark circle with an up-arrow (matches the design). */}
          <button
            ref={startRef}
            type="submit"
            disabled={!valid}
            aria-label="Start demo"
            title="Start demo"
            tabIndex={expanded ? 0 : -1}
            style={{
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: 'none',
              cursor: valid ? 'pointer' : 'not-allowed',
              background: '#1A1816',
              color: '#FFFFFF',
              opacity: valid ? 1 : 0.4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'opacity 0.2s ease',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
          </button>
        </div>
      </form>
      {/* Visible, not just a disabled Start: a real prospect who was wrongly
          refused must be told why the button does nothing. */}
      {collectEmail && emailRefused && expanded && (
        <output
          style={{
            background: '#FFFFFF',
            borderRadius: 12,
            bottom: '100%',
            boxShadow: '0 6px 20px -6px rgba(0,0,0,0.25)',
            color: '#1A1816',
            display: 'block',
            fontSize: 13,
            left: 12,
            marginBottom: 8,
            padding: '6px 12px',
            position: 'absolute',
            right: 12,
          }}
        >
          {getString('demo.introEmailRefused')}
        </output>
      )}
    </div>
  );
};

/**
 * The DEFERRED identity ask — the card that replaces the up-front intro form
 * when a site moves capture into the demo (`after_intro`, `after_first_feature`,
 * `on_walkthrough_request`, `on_end`).
 *
 * Bottom-anchored over the demo rather than modal over it, deliberately: the
 * demo is live and the bot is mid-sentence. A modal would stop a running
 * conversation to collect a lead, which is the interaction that makes an
 * in-demo ask feel like a paywall. This is skippable, one per session.
 *
 * It is a TYPED card, not a spoken ask, for a specific reason: the field that
 * actually matters downstream is the email, and speech-to-text mangles emails
 * (the "@", the dots, spelled-out domains). Name/company/role the Shepherd can
 * absorb conversationally via update_prospect; the email needs an input.
 */
const DemoIntroForm: React.FC<{
  prefilledEmail?: string;
  prefilledProspect?: DemoIntroPrefill;
  /** Resolved capture policy — deferred ⇒ this renders as a Start card only. */
  capture?: DemoCapturePolicy;
  /** The no-email cost gate, for the deferred Start card. */
  onStart?: () => void;
  onEmailCaptured?: (email: string) => void;
  onSubmit: (data: Record<string, string>) => void;
  checkEmail?: EmailCheck;
  /** An address the host or link supplied; never re-checked. */
  trustedEmail?: string;
}> = ({ prefilledEmail, prefilledProspect, capture, onStart, onEmailCaptured, onSubmit, checkEmail, trustedEmail }) => {
  // Stage 1: just email. Stage 2: email confirmed + name (required) +
  // company/role (optional). 'ready': nothing left to ask — a single Start
  // tap. Stage 3 ('done'): success card.
  //
  // onEmailCaptured is the cost gate: the SDK doesn't call /demo/start until it
  // fires, and nothing else does. On the Stage 1 → Stage 2 path it fires on the
  // email advance, so pipecat warms up while the user fills in their name and
  // the bot is usually connected by the time they hit "Start demo". On the
  // prefilled paths there is no typing to warm up under, so it fires from the
  // final submit instead (see handleSubmit).
  //
  // config.prospectEmail arrives here as prefilledEmail. It skips the email
  // STAGE; it does not start anything. A demo link that also carries the
  // prospect's name (prefilledProspect) leaves the form with nothing to
  // collect — planDemoIntro lands on 'ready' and we render the one-tap Start
  // card instead of a form asking for details the link already had.
  //
  // The email is taken from prefilledEmail ONLY — deliberately overriding any
  // prefilledProspect.email. prefilledEmail is the same value that armed the
  // SDK's cost gate; trusting a second, unsynchronised source could skip the
  // email stage for a session that was never actually started.
  const plan = useMemo(
    () => planDemoIntro({ ...prefilledProspect, email: prefilledEmail }, capture),
    [prefilledProspect, prefilledEmail, capture],
  );
  // Seeded once, at mount. Lazy initializers on purpose: after the first render
  // these are the USER's edits, and re-deriving them from the plan would undo
  // typing (or bounce someone who clicked "Edit details" back to the card).
  const [stage, setStage] = useState<'email' | 'rest' | 'ready' | 'done'>(
    () => plan.stage,
  );
  const [values, setValues] = useState<Record<string, string>>(() => plan.values);

  const emailRefused = useEmailRefused(values.email ?? '', checkEmail, trustedEmail);
  const emailValid = !!values.email && EMAIL_RE.test(values.email.trim()) && !emailRefused;
  // `emailRequired` is false only on the deferred path, where the card has no
  // inputs at all. Gating the Start button on `emailValid && name` there would
  // render a card whose one control silently no-ops — the demo would never boot
  // and nothing would say why.
  const normalizedPhone = normalizeIntroPhone(values.phone, capture?.phoneRegion);
  const canStart = plan.emailRequired
    ? emailValid && !!values.name?.trim() && (capture?.requiredFields ?? []).every(field => field === 'phone' ? !!normalizedPhone : !!values[field]?.trim())
    : true;

  const advanceFromEmail = () => {
    if (!emailValid) return;
    setStage('rest');
    // Fire the cost-gate callback. The SDK runs startBotAndConnect
    // asynchronously — this returns immediately so the Stage 2 form
    // renders without waiting on /demo/start latency.
    onEmailCaptured?.(values.email.trim());
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (stage === 'email') {
      advanceFromEmail();
      return;
    }
    if (!canStart) return;
    // Fire the cost gate BEFORE onSubmit, synchronously inside the tap. This is
    // the start signal for every path that never passed through Stage 1 — the
    // prefilled 'ready' card, and 'rest' when the link supplied the email but
    // not the name. Nothing starts the session at init any more, so without
    // this those paths would submit a form and boot nothing.
    //
    // Unconditional on purpose: startDemoSessionAfterEmail is idempotent, so
    // the Stage-1 and docked-pill paths (which already fired it) no-op here
    // rather than needing a "did we already start" flag to drift out of sync.
    //
    // The deferred branch calls the no-email gate: there is nothing to pass,
    // and passing '' would blank a userInfo.email a demo link had supplied.
    if (plan.emailRequired) {
      onEmailCaptured?.(values.email.trim());
    } else {
      onStart?.();
    }
    setStage('done');
    // Still submit whatever the link DID know (name/company/role from a
    // prefill). Under a deferred policy this is usually `{}` — the
    // prospect_prefill handler skips empty updates — but when the link carried
    // context it must still reach the opener, exactly as it does today.
    const { phone: _typedPhone, ...details } = values;
    onSubmit(normalizedPhone ? { ...details, phone: normalizedPhone } : details);
  };

  // ── 'ready': nothing left to ask. Two ways to land here — the link knew both
  // email and name, or the site defers the ask into the demo — and in both the
  // card is a single Start tap with no typing.
  //
  // That tap is the START signal, not a formality: nothing spawns a session at
  // init, so this card is what stands between a page load (or a headless link
  // scanner) and a paid session — and the same gesture acquires the mic. See
  // planDemoIntro for why we don't auto-submit.
  if (stage === 'ready') {
    const firstName = values.name?.split(' ')[0];
    // Everything we already know, shown back so a wrong detail is visible
    // rather than silently driving the whole demo. Empty on the deferred path
    // with no prefill — there is nothing to show back.
    const knownDetails = [values.email, values.phone, values.company, values.role].filter(
      Boolean,
    ) as string[];
    // "Edit details" drops to the Stage-2 form. Only offer it when there is
    // something to correct: on a deferred policy with no prefill it would
    // conjure the exact email+name form the operator configured away, and its
    // Start button would then demand fields nobody was going to be asked for.
    const canEditDetails = plan.emailRequired || knownDetails.length > 0 || !!values.name;
    return (
      <>
        <style>{`
          @keyframes floe-form-rise {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
        <form
          onSubmit={handleSubmit}
          style={{
            fontFamily: FONT_SANS,
            color: COLOR_TEXT,
            animation: 'floe-form-rise 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 10px',
            borderRadius: 999,
            background: COLOR_ACCENT_08,
            marginBottom: 20,
            fontFamily: FONT_SANS,
          }}>
            <span style={{
              width: 5, height: 5, borderRadius: '50%',
              background: ACCENT,
              boxShadow: `0 0 8px ${ACCENT_GLOW}`,
            }} />
            <span lang="en" style={{
              fontSize: 10, fontWeight: 600, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: ACCENT,
            }}>
              Live demo
            </span>
          </div>

          <h2 style={{
            margin: 0,
            fontFamily: FONT_DISPLAY,
            fontSize: 36, fontWeight: 400,
            letterSpacing: '-0.01em', lineHeight: 1.1,
            color: COLOR_TEXT,
          }}>
            {firstName ? `Ready when you are, ${firstName}.` : 'Ready when you are.'}
          </h2>
          <p style={{
            margin: '12px 0 0',
            fontFamily: FONT_SANS,
            fontSize: 15, lineHeight: 1.55,
            color: COLOR_TEXT_MUTED,
            opacity: 0.85,
            maxWidth: '94%',
          }}>
            {/* "personalized" only holds when the link actually carried
                details. On a deferred policy with nothing prefilled it would be
                a claim about a prospect we know nothing about. */}
            {knownDetails.length > 0 || firstName
              ? 'Your personalized demo is ready to start. Tap start and the agent will take you through it — talk to it like a person.'
              : 'Tap start and the agent will take you through a live demo — talk to it like a person.'}
          </p>

          {knownDetails.length > 0 && (
            <p style={{
              margin: '20px 0 0',
              fontFamily: FONT_SANS,
              fontSize: 13, lineHeight: 1.6,
              color: COLOR_TEXT_DIM,
              opacity: 0.85,
            }}>
              {knownDetails.join(' · ')}
            </p>
          )}

          <div style={{ marginTop: 36, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            <button
              type="submit"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${ACCENT_BRIGHT} 0%, ${ACCENT} 100%)`;
                e.currentTarget.style.boxShadow = `0 14px 36px -10px ${ACCENT_GLOW}`;
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${ACCENT} 0%, ${COLOR_ACCENT_STRONG} 100%)`;
                e.currentTarget.style.boxShadow = `0 10px 28px -10px ${ACCENT_GLOW}`;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              style={{
                padding: '13px 24px 13px 26px',
                fontSize: 14, fontWeight: 600,
                background: `linear-gradient(135deg, ${ACCENT} 0%, ${COLOR_ACCENT_STRONG} 100%)`,
                color: COLOR_BG,
                border: 'none', borderRadius: 999,
                cursor: 'pointer',
                boxShadow: `0 10px 28px -10px ${ACCENT_GLOW}`,
                transition: 'all 0.2s cubic-bezier(0.16,1,0.3,1)',
                fontFamily: FONT_SANS,
                display: 'inline-flex', alignItems: 'center', gap: 10,
              }}
            >
              <span>Start demo</span>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
                <path d="M3 8h10m0 0l-4-4m4 4l-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Escape hatch: whoever minted the link typed these details, so
                they can be wrong. Without this the recipient would have no way
                to correct a misspelled name the agent then uses all demo.
                Hidden when there is nothing to correct — see canEditDetails. */}
            {canEditDetails && (
              <button
                type="button"
                onClick={() => setStage('rest')}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  fontFamily: FONT_SANS, fontSize: 13,
                  color: COLOR_TEXT_DIM,
                  opacity: 0.75,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                }}
              >
                Not you? Edit details
              </button>
            )}
          </div>
        </form>
      </>
    );
  }

  if (stage === 'done') {
    return (
      <>
        <style>{`@keyframes floe-form-success { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
        <div style={{
          fontFamily: FONT_SANS,
          display: 'flex', alignItems: 'center', gap: 14,
          animation: 'floe-form-success 0.4s cubic-bezier(0.16,1,0.3,1)',
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            boxShadow: `0 8px 24px -8px ${ACCENT_GLOW}`,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COLOR_BG} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div>
            <div style={{
              fontFamily: FONT_DISPLAY,
              fontSize: 22, fontWeight: 400, lineHeight: 1.1,
              color: COLOR_TEXT,
              letterSpacing: '-0.01em',
            }}>
              {/* Name-free fallback: under a deferred policy this card is
                  reached from a Start tap that asked for nothing, so
                  `values.name` is empty and the old copy rendered "Thanks, ." */}
              {values.name?.trim()
                ? getString('demo.introThanks', { name: values.name.trim().split(' ')[0] })
                : 'You’re all set.'}
            </div>
            <div style={{
              fontSize: 13, color: COLOR_TEXT_MUTED, opacity: 0.85, marginTop: 4,
            }}>
              {values.name?.trim() || values.email?.trim()
                ? getString('demo.starting')
                : 'Starting your demo…'}
            </div>
          </div>
        </div>
      </>
    );
  }

  const onStage2 = stage === 'rest';
  const buttonReady = onStage2 ? canStart : emailValid;

  return (
    <>
      {/* Keyframes — scoped to this component via unique names so they
          don't collide with anything on the host page. */}
      <style>{`
        @keyframes floe-form-rise {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes floe-form-stage {
          from { opacity: 0; transform: translateY(8px); max-height: 0; }
          to { opacity: 1; transform: translateY(0); max-height: 400px; }
        }
      `}</style>

      {/* No card frame. The form floats on the host page's dark backdrop.
          A soft coral ambient glow behind it (rendered in DemoView's
          full-screen modal parent) does the visual grounding without
          needing a visible border around this element. */}
      <form
        onSubmit={handleSubmit}
        style={{
          fontFamily: FONT_SANS,
          color: COLOR_TEXT,
          animation: 'floe-form-rise 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Top row: small "Live" badge floating above the title. Uses the
            same coral-muted background as floe-site's coral chips. */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px',
          borderRadius: 999,
          background: COLOR_ACCENT_08,
          marginBottom: 20,
          fontFamily: FONT_SANS,
        }}>
          <span style={{
            width: 5, height: 5, borderRadius: '50%',
            background: ACCENT,
            boxShadow: `0 0 8px ${ACCENT_GLOW}`,
          }} />
          <span style={{
            fontSize: 10, fontWeight: 600, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: ACCENT,
          }}>
            {getString('demo.liveDemo')}
          </span>
        </div>

        {/* Title — DM Serif Display, the same editorial face floe-site
            uses for hero headlines. Larger than before (32px) to earn its
            keep as the primary moment on the page. */}
        <h2 style={{
          margin: 0,
          fontFamily: FONT_DISPLAY,
          fontSize: 36, fontWeight: 400,
          letterSpacing: '-0.01em', lineHeight: 1.1,
          color: COLOR_TEXT,
        }}>
          {getString(onStage2 ? 'demo.introMoreTitle' : 'demo.emailPrompt')}
        </h2>
        <p style={{
          margin: '12px 0 0',
          fontFamily: FONT_SANS,
          fontSize: 15, lineHeight: 1.55,
          color: COLOR_TEXT_MUTED,
          opacity: 0.85,
          maxWidth: '94%',
        }}>
          {getString(onStage2 ? 'demo.introNameBody' : 'demo.introEmailBody')}
        </p>

        {/* Fields — vertical rhythm > visual hierarchy. */}
        <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 24 }}>
          <FormField
            id="floe-demo-email"
            type="email"
            label={getString('demo.introEmail')}
            placeholder="sarah@company.com"
            value={values.email || ''}
            onChange={(v) => setValues({ ...values, email: v })}
            autoFocus={stage === 'email'}
            size={onStage2 ? 'md' : 'lg'}
          />
          {emailRefused && <output style={{ color: COLOR_TEXT_MUTED, display: 'block', fontSize: 13, margin: 0 }}>{getString('demo.introEmailRefused')}</output>}

          {onStage2 && (
            <>
              <div style={{ animation: 'floe-form-stage 0.45s cubic-bezier(0.16,1,0.3,1)' }}>
                <FormField
                  id="floe-demo-name"
                  label={getString('demo.introName')}
                  placeholder="Sarah Chen"
                  value={values.name || ''}
                  onChange={(v) => setValues({ ...values, name: v })}
                  autoFocus
                />
              </div>

              {(capture?.askFields ?? capture?.fields ?? []).includes('phone') && <FormField
                id="floe-demo-phone" type="tel" label={getString('demo.introPhone')}
                placeholder={capture?.phoneRegion === 'IN' ? '+91 98765 43210' : getString('identity.placeholder.phone')}
                value={values.phone || ''} optional={!capture?.requiredFields?.includes('phone')}
                onChange={(phone) => setValues({ ...values, phone })}
              />}
              {values.phone && !normalizedPhone && <p role="status" style={{ fontSize: 13, color: COLOR_TEXT_MUTED, margin: 0 }}>{getString('demo.introPhoneInvalid')}</p>}

              {/* "Optional" group label — plain text, no divider line,
                  just a lower-weight heading that introduces the next
                  two fields as not required. */}
              <div
                style={{
                  marginTop: 12,
                  animation: 'floe-form-stage 0.45s 0.08s cubic-bezier(0.16,1,0.3,1) both',
                }}
              >
                <p style={{
                  margin: 0,
                  fontFamily: FONT_SANS,
                  fontSize: 11, fontWeight: 600, letterSpacing: '0.14em',
                  textTransform: 'uppercase', color: COLOR_TEXT_DIM,
                }}>
                  {(capture?.requiredFields ?? []).some(field => field === 'company' || field === 'role')
                    ? getString('demo.introMoreTitle') : getString('demo.introOptional')}
                </p>
                <p style={{
                  margin: '4px 0 0',
                  fontFamily: FONT_SANS,
                  fontSize: 13,
                  color: COLOR_TEXT_DIM,
                  opacity: 0.85,
                }}>
                  {getString('demo.introOptionalBody')}
                </p>
              </div>

              <div style={{
                display: 'flex', flexDirection: 'column', gap: 22,
                animation: 'floe-form-stage 0.45s 0.14s cubic-bezier(0.16,1,0.3,1) both',
              }}>
                <FormField
                  id="floe-demo-company"
                  label={getString('demo.introCompany')}
                  placeholder="Acme Inc"
                  value={values.company || ''}
                  optional={!capture?.requiredFields?.includes('company')}
                  onChange={(v) => setValues({ ...values, company: v })}
                />
                <FormField
                  id="floe-demo-role"
                  label={getString('demo.introRole')}
                  placeholder="VP of People"
                  value={values.role || ''}
                  optional={!capture?.requiredFields?.includes('role')}
                  onChange={(v) => setValues({ ...values, role: v })}
                />
              </div>
            </>
          )}
        </div>

        {/* Submit — pill shape, generous top margin. Sits at the bottom
            of the flow as a natural conclusion, not as the bottom of a
            card. */}
        <div style={{ marginTop: 36, display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            type="submit"
            disabled={!buttonReady}
            onMouseEnter={(e) => {
              if (buttonReady) {
                e.currentTarget.style.background = `linear-gradient(135deg, ${ACCENT_BRIGHT} 0%, ${ACCENT} 100%)`;
                e.currentTarget.style.boxShadow = `0 14px 36px -10px ${ACCENT_GLOW}`;
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              if (buttonReady) {
                e.currentTarget.style.background = `linear-gradient(135deg, ${ACCENT} 0%, ${COLOR_ACCENT_STRONG} 100%)`;
                e.currentTarget.style.boxShadow = `0 10px 28px -10px ${ACCENT_GLOW}`;
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
            style={{
              padding: '13px 24px 13px 26px',
              fontSize: 14, fontWeight: 600,
              background: buttonReady
                ? `linear-gradient(135deg, ${ACCENT} 0%, ${COLOR_ACCENT_STRONG} 100%)`
                : 'rgba(245,240,235,0.05)',
              color: buttonReady ? COLOR_BG : 'rgba(245,240,235,0.3)',
              border: 'none', borderRadius: 999,
              cursor: buttonReady ? 'pointer' : 'not-allowed',
              boxShadow: buttonReady ? `0 10px 28px -10px ${ACCENT_GLOW}` : 'none',
              transition: 'all 0.2s cubic-bezier(0.16,1,0.3,1)',
              fontFamily: FONT_SANS,
              display: 'inline-flex', alignItems: 'center', gap: 10,
            }}
          >
            <span>{getString(onStage2 ? 'demo.introStart' : 'demo.introContinue')}</span>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden style={{
              opacity: buttonReady ? 1 : 0.4,
            }}>
              <path d="M3 8h10m0 0l-4-4m4 4l-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {!onStage2 && (
            <span style={{
              fontSize: 12,
              color: COLOR_TEXT_DIM,
              opacity: 0.7,
              fontFamily: FONT_SANS,
            }}>
              or press <kbd style={{
                fontFamily: FONT_SANS, fontSize: 11, fontWeight: 500,
                padding: '2px 6px', borderRadius: 4,
                background: 'rgba(245,240,235,0.06)',
                border: '1px solid rgba(245,240,235,0.08)',
                color: COLOR_TEXT_MUTED,
              }}>↵</kbd>
            </span>
          )}
        </div>
      </form>
    </>
  );
};
